/**
 * @navoiy/domain — the single definition of every cross-channel domain
 * constant. Transcribed from claude-context/backend-spec.md (docs/backend-spec.md
 * in this repo): §2 enums, §3.1 error codes, §3.3 byte caps, §3.4 rate limits,
 * §4.1/§4.2 reference formats, §6.2 mime allow-list.
 *
 * Four consumers: services/api, services/bot, apps/admin, the kiosk repo — and,
 * via packages/tokens' sibling generator, the Flutter app (§9.3).
 *
 * NOTHING here may be duplicated in a consumer. If a value is not in this file
 * it is not a domain constant.
 */
// ---------------------------------------------------------------------------
// §2.1 Enums — values are keys, never display strings. Labels live in
// packages/i18n (`shared.yettiligi.role.<value>` etc.).
// ---------------------------------------------------------------------------
/**
 * §2.1 `channel_t` — every channel an appeal can be STORED under (migration
 * 0022 widened it with `reception`).
 *
 * ===========================================================================
 * THE SPLIT (§14.5.1) — AND WHY IT IS NOT THE SAME LIST AS `SUBMISSION_CHANNELS`.
 * ===========================================================================
 *
 * §12M.3.1 refused `telegram_webapp` because the Mini App and the bot were
 * legally and channel-wise IDENTICAL — only the widget differed, and
 * `client_app` records that. A walk-in at the qabulxona is not that case: the
 * citizen used no kiosk, no app and no Telegram, and filing their appeal as
 * `channel='kiosk'` would corrupt the channel statistic on the very screen
 * §14 builds. The data has to be true before the chart can be.
 *
 * So the enum is widened for STORAGE and RESPONSES, and the list a citizen
 * credential may SUBMIT stays three long — see `SUBMISSION_CHANNELS`. Two
 * constants, because the two questions genuinely have different answers, and a
 * single list would have to answer one of them wrongly.
 */
export const CHANNELS = ['kiosk', 'mobile', 'telegram', 'reception'];
/**
 * §14.5.1 — the channels a CITIZEN CREDENTIAL may submit under.
 *
 * `reception` is absent by construction: it is set server-side on
 * `POST /admin/murojaats` (§14.5.3) and nowhere else. `enforceSubmissionChannel`
 * in `murojaats-submit.ts` refuses a device/install/bot/webapp payload that
 * names it with `422 {field:'channel', code:'not_allowed_for_credential'}`,
 * mirroring `enforceBotScope`.
 *
 * The contract carries the same split: request schema `SubmissionChannel`
 * (three), response schema `Channel` (four). No citizen client can express
 * `reception`, so no citizen client changes behaviour.
 */
export const SUBMISSION_CHANNELS = ['kiosk', 'mobile', 'telegram'];
export function isSubmissionChannel(value) {
    return SUBMISSION_CHANNELS.includes(value);
}
/**
 * §12M.3.1 — `client.app`, the SURFACE that produced a submission.
 *
 * Deliberately NOT a channel. The Telegram Mini App and the conversational bot
 * are one channel (`telegram`), one legal identification basis and one §8.3 row
 * family; adding `telegram_webapp` to `channel_t` would be a Postgres enum
 * migration plus a widened RESPONSE enum in the contract — breaking for four
 * generated clients — to record which widget a citizen tapped. This value is
 * carried in every payload already and is now stored (`murojaats.client_app`),
 * which closes an existing blind spot rather than opening a new axis.
 *
 * §14.5.1 adds `admin`: the panel IS a fifth surface, and a reception appeal
 * filed from it must be distinguishable from one a kiosk queued. Note that
 * `admin` is a surface and `reception` is a channel — the walk-in is the
 * channel fact, the panel is merely what typed it.
 */
export const CLIENT_APPS = ['kiosk', 'mobile', 'bot', 'webapp', 'admin'];
export const MUROJAAT_KINDS = ['voice', 'video', 'text'];
export const MUROJAAT_STATUSES = ['new', 'in_review', 'answered', 'closed'];
export const RESOLUTIONS = ['answered', 'left_without_review', 'terminated', 'forwarded'];
export const IDENTIFICATION_MODES = ['spoken', 'typed', 'telegram_contact'];
/**
 * DELTA N-F — WHERE `murojaats.author_name` CAME FROM. Provenance, not identity.
 *
 * ===========================================================================
 * THIS IS NOT `identification_mode`, AND IT MUST NEVER BECOME IT.
 * ===========================================================================
 * `identification_mode` is the statutory Art. 6 fact — how the citizen
 * identified themselves for this appeal — and it stays `spoken` for every
 * kiosk voice/video appeal, confirmed name or not. A name the citizen heard
 * back and approved is a BETTER SPELLING, not verified identity: nothing
 * checked it against a registry, and four clients share the Postgres enum
 * behind that column. Widening it would silently promote a machine's reading
 * of a recording into an identity assertion.
 *
 * So the provenance is its own column with its own four values, and its only
 * consumer is a reader deciding how much to trust the string:
 *
 *   `extracted`           a model pulled it out of the appeal transcript and
 *                         nobody confirmed it — today's status quo, and the
 *                         reason appeals were filed under wrong names.
 *   `spoken_unconfirmed`  the citizen re-said their name into a clip, but never
 *                         saw text they approved. The CLIP is attached; the
 *                         name (if any) is still whatever the chain resolved.
 *   `spoken_confirmed`    the citizen saw this exact string on screen and
 *                         tapped «Ha». The strongest spoken form there is.
 *   `typed`               somebody typed it — the citizen into this appeal or
 *                         their profile, or staff at the reception desk.
 *
 * ABSENT/NULL MEANS `extracted` FOR A DRAFT-DERIVED NAME. An older kiosk that
 * knows nothing about this feature sends no `name_source` and stays correct.
 */
export const NAME_SOURCES = [
    'extracted',
    'spoken_unconfirmed',
    'spoken_confirmed',
    'typed',
];
/**
 * The subset a CLIENT may assert. `typed` is not in it, deliberately: a client
 * saying "the citizen typed this" adds nothing the server cannot see for itself
 * (the name arrived in `author_name`), and a client that could claim it could
 * dress an extracted name as a typed one.
 */
export const SPOKEN_NAME_SOURCES = [
    'extracted',
    'spoken_unconfirmed',
    'spoken_confirmed',
];
export function isSpokenNameSource(value) {
    return typeof value === 'string' && SPOKEN_NAME_SOURCES.includes(value);
}
/** DELTA N-F — a name is at most this many whitespace-separated tokens. */
export const NAME_MAX_TOKENS = 5;
/**
 * IS THIS ANSWER SHAPED LIKE A NAME AT ALL?
 *
 * The name-clip pass asks a model for one string and gets back whatever the
 * model felt like returning. Three refusals, and every one of them is a real
 * observed failure mode of asking a model for a name:
 *
 *   - MORE THAN FIVE TOKENS — the model transcribed the sentence ("Mening ismim
 *     Karimov Aziz Abdurashidovich boʻladi") instead of extracting the name.
 *     Five is the ceiling a real Uzbek FIO needs (surname, given name,
 *     patronymic, plus two for a double-barrelled or particled form).
 *   - ANY DIGIT — the model reached past the name into a house number, a phone
 *     number or a year. A name has no digits, in any of our four locales.
 *   - LONGER THAN 120 CHARACTERS — `AUTHOR_NAME_MAX_CHARS`, which is also the
 *     kiosk's own refusal threshold (DELTA N-F3.3). A string this long is a
 *     sentence, and a citizen must never be asked to approve a sentence as
 *     their name.
 *
 * Refusing is SAFE: the appeal is filed, the clip is attached, and a staff
 * member listens to six seconds of audio. Accepting a wrong string is not.
 *
 * IT BELONGS TO THE WORKER, NOT TO THE API. This gate is applied where model
 * output is FIRST SEEN — before a citizen has read it — because invention is what
 * it catches. It is deliberately NOT re-applied when the appeal is submitted: by
 * then a human has read the string at 72 px and tapped «Ha», and a second gate
 * there could not change what gets stored (a lower link of the resolution chain
 * stores it anyway) and could only falsify its provenance to `extracted`, i.e.
 * "nobody confirmed this" about a name the citizen personally confirmed.
 *
 * It lives in the domain rather than in the worker so that its reasoning is
 * stated once, and so the kiosk's own 120-character refusal and this one cannot
 * drift apart on the number they share.
 */
export function isPlausibleSpokenName(value) {
    if (typeof value !== 'string')
        return false;
    const trimmed = value.trim();
    if (trimmed.length === 0 || trimmed.length > AUTHOR_NAME_MAX_CHARS)
        return false;
    if (/[0-9٠-٩۰-۹]/u.test(trimmed))
        return false;
    return trimmed.split(/\s+/u).length <= NAME_MAX_TOKENS;
}
/** §2.1 `yettiligi_role_t` — order is the display order of the eight cards. */
export const YETTILIGI_ROLES = [
    'raisi',
    'hokim_yordamchisi',
    'yoshlar_yetakchisi',
    'xotin_qizlar_faoli',
    'profilaktika_inspektori',
    'ijtimoiy_xodim',
    'soliq_inspektori',
    'banker',
];
export const SCAN_STATUSES = ['pending', 'clean', 'infected', 'skipped'];
/**
 * §2.6 `admin_role_t`. The first four are the murojaat panel's roles; the six
 * after them are MBT's (`MBT-project-overview.md` §1.3), added by migration
 * 0023.
 *
 * ONE ROLE PER ACCOUNT, still. MBT §1.3 says "a user may hold several", and the
 * honest reading of that against this schema is that `admin_users.role` is a
 * single enum column, every capability check already goes through `can()`, and
 * multi-role would mean a join table plus a rewrite of every `requireCapability`
 * call site — a change to the murojaat panel's authorization, which MBT is
 * forbidden to touch. Recorded in `docs/mbt-progress.md` as decision D-MBT-01:
 * one role per account in P0; if an official genuinely needs two, they get the
 * union role or a second account, and multi-role is revisited when a real user
 * needs it rather than in advance.
 *
 * ORDER IS NOT RANK — see the §7.2 note above `ADMIN_CAPABILITIES`. Appending
 * six values to this array grants nothing to anybody: a role holds exactly the
 * capabilities that name it, and no existing capability's list was touched.
 */
export const ADMIN_ROLES = [
    'viewer',
    'handler',
    'content_editor',
    'admin',
    // -- MBT (Markaziy boshqaruv tizimi) ------------------------------------
    /** Shahar hokimi — the calm executive home, and the authority to broadcast. */
    'governor',
    /** MBT operator / dispatcher — the dense working console. */
    'operator',
    /** Technician — the alert queue, kiosk health, rollout. */
    'technician',
    /** HR / kadrlar — attendance boards, timesheets, staff records. */
    'hr',
    /** Mahalla rahbari — their own mahalla and nothing else. */
    'mahalla_lead',
    /** Content manager — ads, announcements, campaign scheduling. */
    'content',
];
/**
 * The six roles above that exist for MBT and hold no murojaat capability.
 *
 * Used by the murojaat panel to redirect such an account to `/markaziy/` instead of
 * greeting it with a forbidden notice on a screen it never asked for, and by
 * `apps/command` to decide which home to render (§15.7).
 */
export const MBT_ROLES = [
    'governor',
    'operator',
    'technician',
    'hr',
    'mahalla_lead',
    'content',
];
export function isMbtRole(role) {
    return MBT_ROLES.includes(role);
}
/**
 * `announcement-media` is the third (migration 0035) and it exists because the
 * RETENTION policy differs from both others, not because the content does. A
 * headshot lives for years; an emergency poster is dead weight the hour after
 * its notice expires. Naming it separately is what lets a sweep find one
 * without having to tell it apart from the other by guessing at its key.
 */
export const MEDIA_BUCKETS = ['citizen-media', 'staff-photos', 'announcement-media'];
/** §2.5 `locale` CHECK constraint — the four shipped locales, in kiosk order. */
export const LOCALES = ['uz-Latn', 'uz-Cyrl', 'ru', 'en'];
export const DEFAULT_LOCALE = 'uz-Latn';
/** §2.6 `app_installs.platform` CHECK constraint. */
export const INSTALL_PLATFORMS = ['android', 'ios'];
/**
 * §2.6 `audit_log.actor_kind` CHECK constraint.
 *
 * `webapp` is §12M.1.5's addition: a Mini App submission is audited as
 * `actor_kind='webapp'`, `actor_id=<telegram_user_id>`. It is a separate actor
 * from `bot` because the two surfaces authenticate differently (a `nvw_`
 * session bound to one citizen vs. the bot's service token), and an audit trail
 * that could not tell them apart would be unable to answer "which surface filed
 * this" — the one question §12M.3.1 declines to answer with a new channel.
 */
export const ACTOR_KINDS = ['admin', 'device', 'bot', 'webapp', 'system'];
// ---------------------------------------------------------------------------
// §4.1 / §4.2 Reference numbers
// ---------------------------------------------------------------------------
/** §4.1 — no I/O/0/1, so a code can be read aloud at a counter. */
export const REFERENCE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
/** §4.1 canonical, server-issued: `NV-YYYYMMDD-XXXX`, 16 chars. */
export const REFERENCE_RE = /^NV-\d{8}-[ABCDEFGHJKLMNPQRSTUVWXYZ23456789]{4}$/;
/** §4.2 provisional, kiosk-offline: `NVQ-<DEV>-<XXXX>`, 12 chars. */
export const PROVISIONAL_RE = /^NVQ-[ABCDEFGHJKLMNPQRSTUVWXYZ23456789]{3}-[ABCDEFGHJKLMNPQRSTUVWXYZ23456789]{4}$/;
/** §2.6 `devices.short_code` — the `<DEV>` segment of an `NVQ-` code. */
export const DEVICE_SHORT_CODE_RE = /^[ABCDEFGHJKLMNPQRSTUVWXYZ23456789]{3}$/;
/**
 * §5 one-time enrollment code alphabet — the SAME no-I/O/0/1 alphabet as the
 * reference suffix, because a technician reads this aloud off a screen too.
 * Declared as an alias rather than a second literal so the two can never drift.
 */
export const ENROLL_CODE_ALPHABET = REFERENCE_ALPHABET;
export const ENROLL_CODE_RE = /^[ABCDEFGHJKLMNPQRSTUVWXYZ23456789]{8}$/;
/** §4.2 — base32 of a per-device monotonic counter, 0 … 1 048 575 (32^4 - 1). */
export const PROVISIONAL_COUNTER_MAX = 1_048_575;
/** §3.3 `POST /murojaats/status` accepts either form. */
export function isReference(value) {
    return REFERENCE_RE.test(value);
}
export function isProvisionalCode(value) {
    return PROVISIONAL_RE.test(value);
}
export function isAnyReference(value) {
    return isReference(value) || isProvisionalCode(value);
}
// ---------------------------------------------------------------------------
// Phone — §2.5 CHECK constraint, kiosk design-spec CALLBACK-NUMBER DELTA G1/G5
// ---------------------------------------------------------------------------
/** §2.5 `murojaats.phone` CHECK — the stored, canonical form. */
export const PHONE_RE = /^\+998[0-9]{9}$/;
/** §8.1 step 2 — what the bot accepts a user to *type* before normalisation. */
export const PHONE_INPUT_RE = /^\+?998[0-9]{9}$/;
export const PHONE_PREFIX = '+998';
export const PHONE_DIGITS = 9;
/** Normalises any accepted input form to the stored `+998XXXXXXXXX`. */
export function normalizePhone(input) {
    const digits = input.replace(/\D/g, '');
    const national = digits.startsWith('998') ? digits.slice(3) : digits;
    if (national.length !== PHONE_DIGITS)
        return null;
    const phone = `${PHONE_PREFIX}${national}`;
    return PHONE_RE.test(phone) ? phone : null;
}
/** §2.5 `phone_tail` generated column: `right(phone, 4)`. */
export function phoneTail(phone) {
    return phone.slice(-4);
}
/** §8.1 step 4 confirmation card: `+998 ** *** ** 21`. */
export function maskPhone(phone) {
    return `${PHONE_PREFIX} ** *** ** ${phoneTail(phone).slice(-2)}`;
}
/** Kiosk/mobile display mask `+998 XX XXX XX XX` — identical in all four
 * locales (never an i18n key: it is a formatted numeral). */
export function formatPhone(phone) {
    const d = phone.replace(/\D/g, '').slice(-PHONE_DIGITS);
    return `${PHONE_PREFIX} ${d.slice(0, 2)} ${d.slice(2, 5)} ${d.slice(5, 7)} ${d.slice(7, 9)}`;
}
/** §3.3 `POST /murojaats/status` body field. */
export const PHONE_TAIL_RE = /^[0-9]{4}$/;
// ---------------------------------------------------------------------------
// §3.3 Byte and length caps — server-enforced, hard.
// ---------------------------------------------------------------------------
export const KIB = 1024;
export const MIB = 1024 * 1024;
/** ≤ 12 MiB — aligned with kiosk platform-spec §2.1 MODE.voice.maxBytes. */
export const VOICE_MAX_BYTES = 12 * MIB; // 12 582 912
/** ≤ 64 MiB — aligned with kiosk platform-spec §2.1 MODE.video.maxBytes. */
export const VIDEO_MAX_BYTES = 64 * MIB; // 67 108 864
/**
 * OWNER RULING 2026-09-27 — the optional attachment on a TEXT murojaat: one
 * photo, or one short video. 12 MiB, the same ceiling as a voice recording.
 *
 * It is deliberately NOT `VIDEO_MAX_BYTES`. A 64 MiB cap on an attachment a
 * citizen picks out of their gallery is an invitation to upload a two-minute
 * 4K clip over mahalla 4G, and the appeal it is attached to is text — the
 * evidence is a picture of a pothole or thirty seconds of a leaking pipe, not
 * a filmed statement. 12 MiB is a full-resolution phone photo with room to
 * spare, and it keeps the client-side refusal honest: a citizen is told before
 * the upload starts rather than after 50 MiB of their data allowance.
 *
 * A text appeal with an attachment is STILL `kind: 'text'`. There is one
 * `murojaats.media_id` slot and this uses it, which is why the 0007
 * `media_needed` CHECK (`kind = 'text' OR media_id IS NOT NULL`) already
 * permits it and no migration was needed.
 */
export const TEXT_ATTACHMENT_MAX_BYTES = 12 * MIB; // 12 582 912
/**
 * DELTA N-F — the kiosk's NAME CONFIRMATION clip (§5.2(n)).
 *
 * A citizen who sees the wrong name re-says ONLY their name, into a clip of at
 * most six seconds. 512 KiB is three orders of magnitude below the appeal's
 * ceiling, and that is the point rather than a saving: the cap is what makes the
 * clip unable to be anything else. Six seconds of Opus at 48 kbps is ~36 KB, so
 * the cap has ~14× headroom and still cannot hold a second appeal — a recorder
 * bug that kept running is refused by byte count before it can turn a one-field
 * confirmation into an unreviewed, unconsented second recording of the citizen.
 *
 * The clip is the SAME CLASS OF DATA as the appeal recording: same bucket, same
 * retention class, same audit discipline, destroyed in the same operation. Only
 * its size and its lifetime differ.
 */
export const NAME_CLIP_MAX_BYTES = 512 * KIB; // 524 288
/** §5.2(n) recorder caps — the kiosk enforces them; the server re-states them. */
export const NAME_CLIP_MAX_MS = 6_000;
export const NAME_CLIP_MIN_MS = 600;
/** Fastify `bodyLimit` for `POST /murojaats`. */
export const REQUEST_MAX_BYTES = 68 * MIB; // 71 303 168
/**
 * The JSON `payload` part of `POST /murojaats`.
 *
 * A maximal legitimate payload is roughly 4.5 KiB (a 4000-character body plus a
 * 120-character name and a 300-character address), so 64 KiB is ~14× headroom
 * and still small enough to hold in memory. The cap is NOT cosmetic: the payload
 * part is read BEFORE the idempotency pre-check and therefore before the rate
 * limiter, so an uncapped part is heap a caller can allocate for free — six
 * concurrent 64 MiB parts reached 482 MiB RSS. Enforced by byte count while the
 * part streams, never after buffering it.
 */
export const PAYLOAD_MAX_BYTES = 64 * KIB; // 65 536
/** §3.3 `POST /admin/yettiligi/{id}/photo`. */
export const STAFF_PHOTO_MAX_BYTES = 2 * MIB; // 2 097 152
export const BODY_MIN_CHARS = 20;
export const BODY_MAX_CHARS = 4000;
export const AUTHOR_NAME_MAX_CHARS = 120;
export const AUTHOR_ADDRESS_MAX_CHARS = 300;
/** §2.5 lifecycle: `in_review` → `answered` requires `answer_body` ≥ 10 chars. */
export const ANSWER_BODY_MIN_CHARS = 10;
export const ANSWER_BODY_MAX_CHARS = 8000;
/**
 * Owner directive — the first-open onboarding profile (`InstallProfileRequest`,
 * `telegram_users`/`app_installs` migration 0043). Separate from the
 * murojaat-scoped `AUTHOR_*` limits above: this is a standing profile field,
 * not one appeal's Art. 6 identification.
 */
export const PROFILE_FULL_NAME_MIN_CHARS = 3;
export const PROFILE_FULL_NAME_MAX_CHARS = 200;
export const PROFILE_ADDRESS_MIN_CHARS = 3;
export const PROFILE_ADDRESS_MAX_CHARS = 500;
export const PROFILE_NOTE_MAX_CHARS = 1000;
/** §8.2 — Telegram itself will not serve a bot a file larger than this (F-12). */
export const TELEGRAM_BOT_DOWNLOAD_MAX_BYTES = 20 * 1000 * 1000;
export function maxBytesForKind(kind) {
    switch (kind) {
        case 'voice':
            return VOICE_MAX_BYTES;
        case 'video':
            return VIDEO_MAX_BYTES;
        case 'text':
            // Was 0, which made a text appeal's attachment impossible to express:
            // the streaming byte counter tripped on the first chunk and answered
            // "Media exceeds the 0-byte cap for a text murojaat" — a 413 whose text
            // is nonsense. The cap is now the owner's 12 MiB (OWNER RULING
            // 2026-09-27); the attachment stays OPTIONAL, which is a presence rule
            // the route holds, not a byte cap.
            return TEXT_ATTACHMENT_MAX_BYTES;
    }
}
/** The hard byte cap for one captured part, by capture profile. */
export function maxBytesForCapture(kind) {
    return kind === 'name' ? NAME_CLIP_MAX_BYTES : maxBytesForKind(kind);
}
// ---------------------------------------------------------------------------
// Capture behaviour — the constants the kiosk already ships (platform-spec
// §2.1) and that §9.1 binds the Flutter app to.
// ---------------------------------------------------------------------------
export const CAPTURE = {
    voice: { maxMs: 120_000, maxBytes: VOICE_MAX_BYTES, audioBps: 48_000 },
    video: { maxMs: 90_000, maxBytes: VIDEO_MAX_BYTES, audioBps: 64_000, videoBps: 1_500_000 },
};
export const MIN_CAPTURE_MS = 1_500;
export const MIN_CAPTURE_BYTES = 2_048;
/** §9.1 countdown thresholds: amber at 15 s, numeral scale-up at 5 s. */
export const ENDING_SOON_MS = 15_000;
export const SCALE_NUMERAL_MS = 5_000;
// ---------------------------------------------------------------------------
// §6.2 Media mime allow-list — the union of the kiosk's AUDIO_MIME_ORDER /
// VIDEO_MIME_ORDER and Telegram's voice `audio/ogg` + video-note `video/mp4`.
// ---------------------------------------------------------------------------
export const AUDIO_MIME_ALLOWLIST = [
    'audio/webm',
    'audio/webm;codecs=opus',
    'audio/ogg',
    'audio/ogg;codecs=opus',
    'audio/mp4',
];
export const VIDEO_MIME_ALLOWLIST = [
    'video/webm',
    'video/webm;codecs=vp9,opus',
    'video/webm;codecs=vp8,opus',
    'video/mp4',
];
/**
 * OWNER RULING 2026-09-27 — the image half of a text murojaat's attachment.
 *
 * Three formats, because those are the three a phone gallery actually yields:
 * JPEG from every camera, PNG from every screenshot, WebP from anything that
 * passed through a chat app. Every one of them is re-encoded to JPEG on ingest
 * (`sanitiseCitizenImage`), so this list is what the server will PARSE, not
 * what it will store.
 *
 * HEIC is deliberately absent: `sharp` needs a libheif build the API image does
 * not carry, and both mobile pickers hand us a JPEG instead when asked.
 */
export const IMAGE_MIME_ALLOWLIST = ['image/jpeg', 'image/png', 'image/webp'];
/**
 * What may ride along on a `kind: 'text'` appeal: one photo, or one MP4.
 *
 * MP4 ONLY for the video half, not the whole `VIDEO_MIME_ALLOWLIST`. WebM is in
 * that list because the KIOSK's MediaRecorder produces it; a phone's camera and
 * gallery produce MP4, and this attachment exists only on the mobile path. A
 * format nothing can send is a format nothing tests.
 */
export const TEXT_ATTACHMENT_MIME_ALLOWLIST = [...IMAGE_MIME_ALLOWLIST, 'video/mp4'];
export const MEDIA_MIME_ALLOWLIST = [
    ...AUDIO_MIME_ALLOWLIST,
    ...VIDEO_MIME_ALLOWLIST,
    ...IMAGE_MIME_ALLOWLIST,
];
/** §3.3 `POST /admin/yettiligi/{id}/photo`: JPEG/PNG only. */
export const STAFF_PHOTO_MIME_ALLOWLIST = ['image/jpeg', 'image/png'];
export function isAllowedMediaMime(mime) {
    return MEDIA_MIME_ALLOWLIST.includes(mime);
}
/**
 * A declared `kind` must agree with the mime family (§6.2 step 2).
 *
 * `text` used to return `false` unconditionally — "a text appeal has no media",
 * which was true until the owner ruled otherwise. It now accepts exactly the
 * attachment allow-list: an image, or an MP4. `voice` and `video` are unchanged,
 * and in particular a text appeal's allow-list does NOT leak into them — a
 * `kind: 'voice'` submission carrying a JPEG is still a 415, because the
 * recording IS the appeal there and a picture cannot be one.
 */
export function mimeMatchesKind(mime, kind) {
    if (kind === 'text') {
        return TEXT_ATTACHMENT_MIME_ALLOWLIST.includes(mime);
    }
    const family = kind === 'voice' ? 'audio/' : 'video/';
    return mime.startsWith(family) && isAllowedMediaMime(mime);
}
/**
 * The mime gate for one captured part, by capture profile.
 *
 * A NAME CLIP IS AUDIO-ONLY IN BOTH CAPTURE MODES, including after a video
 * appeal (DELTA N-F4.2: the kiosk shows a microphone, not a camera, and a camera
 * glyph there would be a promise the recorder does not keep). So the profile
 * reuses `voice`'s allow-list rather than the calling appeal's kind — a citizen
 * who filed by video does not get filmed again to spell their surname.
 */
export function mimeMatchesCapture(mime, kind) {
    return mimeMatchesKind(mime, kind === 'name' ? 'voice' : kind);
}
/**
 * §6.2 step 2 magic-byte prefixes, hex. EBML (webm), OggS (ogg), ftyp box (mp4),
 * SOI (jpeg), the 8-byte PNG signature, and RIFF…WEBP.
 *
 * This is the check a malicious client cannot forge by editing a header field,
 * which is exactly why the image formats had to be added here and not only to
 * the allow-list: without a signature to compare against, `image/png` would be
 * a mime any bytes at all could claim.
 */
export const MAGIC_BYTES = {
    webm: [0x1a, 0x45, 0xdf, 0xa3],
    ogg: [0x4f, 0x67, 0x67, 0x53],
    /** `ftyp` lives at offset 4 of an ISO-BMFF file. */
    mp4: [0x66, 0x74, 0x79, 0x70],
    mp4Offset: 4,
    /** `FF D8 FF` — JPEG SOI plus the first marker byte. */
    jpeg: [0xff, 0xd8, 0xff],
    /** The full 8-byte PNG signature, including the CR/LF/EOF trap bytes. */
    png: [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a],
    /** RIFF container… */
    webpRiff: [0x52, 0x49, 0x46, 0x46],
    /** …with `WEBP` at offset 8, after the 4-byte little-endian file size. */
    webp: [0x57, 0x45, 0x42, 0x50],
    webpOffset: 8,
};
/**
 * The stored resolution ceiling and JPEG quality for a citizen's photo.
 *
 * `fit: 'inside'` with `withoutEnlargement` at 2560 px on the long edge, which
 * is chosen for the two things these photos actually are: a defect (a pothole,
 * a burst pipe, an unlit street) where 2560 px is far more than enough, and a
 * PHOTOGRAPHED DOCUMENT — a handwritten complaint, a utility bill, a notice on
 * a door — where the handler has to read the text. 1080 px, the kiosk
 * announcement bound, loses that; 2560 px keeps it while still cutting a 12 MiB
 * phone original to a few hundred KiB.
 *
 * NOT a crop. `cover` would crop, and what it crops is whatever sits near an
 * edge, which on a photographed document is a signature or a date.
 */
export const CITIZEN_IMAGE_MAX_EDGE = 2560;
export const CITIZEN_IMAGE_JPEG_QUALITY = 82;
// ---------------------------------------------------------------------------
// §6.1 Storage key layout
// ---------------------------------------------------------------------------
export const STORAGE_KEY_PREFIX = {
    citizenMedia: 'citizen',
    staffPhotos: 'staff',
};
// ---------------------------------------------------------------------------
// §2 Time — every date component and statutory deadline is Asia/Tashkent.
// ---------------------------------------------------------------------------
export const TIMEZONE = 'Asia/Tashkent';
/**
 * F-6 Art. 28 — THE LAW'S OUTER LIMIT: applications and complaints are reviewed
 * within 15 days.
 *
 * This is the figure a court reads, and the only one that may ever be described
 * as a legal requirement. It is NOT what `due_at` carries: the hokimlik binds
 * itself to `RESPONSE_DAYS`, which is shorter. The statutory number stays a
 * first-class constant because the statutory fact still has to be stateable —
 * the caption beside the panel's on-time tile, a compliance argument, and the
 * F-6 extension ceiling (`MAX_EXTENSION_DAYS`) all reason about the law's
 * window rather than ours.
 */
export const STATUTORY_REVIEW_DAYS = 15;
/**
 * THE HOKIMLIK'S OPERATIONAL COMMITMENT — owner ruling, 2026-09-28. Every
 * appeal's `due_at` is `received_at` + 5 days (`dueAtFrom`).
 *
 * A promise this body made, not a rule the law imposes: the law allows 15
 * (`STATUTORY_REVIEW_DAYS`). It is deliberately STRICTER, so an appeal that
 * meets it always satisfies F-6, and missing it is a broken promise rather than
 * an illegality. Any text rendering this number must say whose promise it is —
 * "the hokimlik answers within 5 days", never "the law requires 5 days".
 */
export const RESPONSE_DAYS = 5;
/** F-6 — extendable by no more than one further month, once only. */
export const MAX_EXTENSION_DAYS = 31;
// ---------------------------------------------------------------------------
// §3.1 Error model
// ---------------------------------------------------------------------------
export const ERROR_CODES = [
    'validation_failed',
    'unauthorized',
    'forbidden',
    'not_found',
    'rate_limited',
    'payload_too_large',
    'unsupported_media_type',
    'invalid_transition',
    'idempotency_conflict',
    /**
     * §6.4 — the admin player asks for a byte range the object does not have
     * (a stale `Content-Length`, a seek past the end). RFC 9110 §15.5.17 answers
     * that with 416 and a `Content-Range` naming the complete length; answering 503
     * instead told the panel the whole service was down.
     */
    'range_not_satisfiable',
    'service_unavailable',
];
export const ERROR_TYPE_BASE = 'https://navoiy.uz/errors/';
export const ERROR_STATUS = {
    validation_failed: 422,
    unauthorized: 401,
    forbidden: 403,
    not_found: 404,
    rate_limited: 429,
    payload_too_large: 413,
    unsupported_media_type: 415,
    invalid_transition: 409,
    idempotency_conflict: 409,
    range_not_satisfiable: 416,
    service_unavailable: 503,
};
// ---------------------------------------------------------------------------
// §3.2 Idempotency
// ---------------------------------------------------------------------------
export const IDEMPOTENCY_HEADER = 'Idempotency-Key';
export const IDEMPOTENCY_REPLAYED_HEADER = 'Idempotency-Replayed';
/** §3.2 step 4 — keys are honoured for 30 days, then GC'd. */
export const IDEMPOTENCY_TTL_DAYS = 30;
/** §3.2.1 — the exact field set whose canonical JSON is hashed into
 *  idempotency_records.payload_hash. Order is JCS order (sorted keys). */
export const IDEMPOTENCY_HASHED_FIELDS = [
    'author_address',
    'author_name',
    'body',
    'channel',
    'client_code',
    'consent_text_sha256',
    'consent_version',
    'identification_mode',
    'kind',
    'locale',
    'mahalla_id',
    'phone',
    'submission_id',
    'submitted_at',
];
/**
 * Returns the RFC 8785 canonical JSON string to hash. Every hashed value is a
 * string, so sorted-key JSON.stringify IS the canonical form.
 *
 * §3.2.1 excludes, deliberately and non-stylistically:
 *  - `client.app` / `client.version` — a kiosk auto-updates (D-025) while an
 *    item sits in the 14-day queue (§4.4); including the version would turn an
 *    honest retry into a 409 and let electron-updater destroy queued appeals;
 *  - `duration_ms_reported` — advisory only (§2.4), a client may recompute it;
 *  - media bytes — hashed separately as `media_objects.sha256`;
 *  - every header incl. `X-Telegram-User-Id` — credential scope, not content;
 *  - `draft_id` — a per-attempt handle on a recording, not part of what was
 *    submitted: a kiosk whose draft expired retries WITHOUT it and uploads the
 *    recording instead, and that retry must replay rather than 409;
 *  - `name_source` and `name_clip_draft_id` (DELTA N-F) — EXACTLY the same
 *    reasoning as `draft_id`, and it is load-bearing rather than tidy. A name
 *    draft lives 30 minutes; `POST /murojaats` answers `422
 *    name_clip_draft_id/unknown_draft` for an expired one, and the kiosk's only
 *    honest recovery is to resend the same appeal without the clip. If either
 *    field were hashed, that retry would hash differently from the first attempt
 *    and come back `409 idempotency_conflict` — the citizen standing at the
 *    kiosk would be told their appeal was already filed under a different
 *    payload, when in fact it was never filed at all. A kiosk that also sends
 *    the confirmed string as `author_name` has it hashed by that field, which is
 *    the recommended wire; a kiosk that relies on the server resolving it from
 *    the clip draft loses nothing, because the draft row is written once and
 *    never rewritten, so the same id can only ever resolve to the same string.
 */
export function canonicalSubmissionJson(payload) {
    const out = {};
    for (const field of IDEMPOTENCY_HASHED_FIELDS) {
        const value = payload[field];
        if (value !== undefined && value !== null)
            out[field] = String(value);
    }
    return JSON.stringify(out); // keys inserted in sorted order above
}
// ---------------------------------------------------------------------------
// §2.5 Status lifecycle — the allowed transitions, as data.
// ---------------------------------------------------------------------------
export const ALLOWED_TRANSITIONS = {
    new: ['in_review', 'closed'],
    in_review: ['answered', 'in_review', 'closed'],
    answered: ['closed'],
    /** Only `admin` may reopen; the role check is the API's, not this table's. */
    closed: ['in_review'],
};
export function isAllowedTransition(from, to) {
    return ALLOWED_TRANSITIONS[from].includes(to);
}
/** §2.5 — `closed` always carries a resolution. */
export const CLOSING_RESOLUTIONS = [
    'answered',
    'left_without_review',
    'terminated',
    'forwarded',
];
// ---------------------------------------------------------------------------
// §14.3 (D-044) — the Art. 6 statutory classification.
// ---------------------------------------------------------------------------
/**
 * `murojaats.legal_type` — STAFF-SET and authoritative (§14.3.1).
 *
 * Never citizen-selected: a citizen who writes "ariza" at the top of a
 * complaint has not made it an ariza, and the classification is the receiving
 * body's legal determination. Never model-written either: round C may SUGGEST
 * one, and accepting a suggestion is a staff act recorded as staff-set.
 */
export const LEGAL_TYPES = ['ariza', 'shikoyat', 'taklif'];
/**
 * §14.3.4 — the FOUR buckets every legal-type statistic reports.
 *
 * `unclassified` is a first-class bucket, never hidden, never proportionally
 * redistributed and never excluded from the denominator. A legal-type pie that
 * silently omits a third of the appeals is a false official report, so the
 * fourth key is part of the domain rather than a rendering decision a screen
 * could forget to make.
 */
export const LEGAL_TYPE_BUCKETS = [...LEGAL_TYPES, 'unclassified'];
/**
 * §14.3.3 — the one resolution exempt from the classification gate.
 *
 * An appeal answered or closed must carry a statutory type, EXCEPT when it was
 * closed `left_without_review`: F-5 says such an appeal was never reviewed, and
 * an appeal nobody reviewed is an appeal nobody may classify. Naming the
 * exemption here keeps the API gate and the panel's inline prompt reading the
 * same rule.
 */
export const LEGAL_TYPE_EXEMPT_RESOLUTION = 'left_without_review';
// ---------------------------------------------------------------------------
// §14.4 (D-044 round C) — AI Nazorat va Tahlil.
//
// ===========================================================================
// EVERY LABEL SET HERE IS CLOSED, AND THAT IS THE WHOLE SAFETY PROPERTY.
// ===========================================================================
//
// §14.4.1 rules out a generated summary of an appeal precisely because free
// text is where a model invents a fact into a case file. What remains is
// classification into sets that are fixed HERE, in the domain, and enumerated
// in the `responseSchema` the worker sends to Vertex: a model can return a
// wrong code from these lists, but it cannot return a code that does not exist,
// and it can never return a sentence.
//
// The four sets below are therefore load-bearing rather than illustrative. A
// value that is not in one of them is dropped by the worker before it reaches
// `murojaat_ai`, so the database cannot hold a machine-invented label.
// ---------------------------------------------------------------------------
/**
 * §14.4.1 — the CLOSED 12-code municipal topic set, ≤ 3 per appeal.
 *
 * Codes only. Every label is `admin.ai.topic.<code>` in all four locales, so a
 * topic reads as the same word to a Russian handler and an Uzbek one, and so
 * the model never sees a display string it might try to be creative with.
 *
 * Scoped to what a HOKIMIYAT actually receives. Two boundaries worth stating
 * because they are the ones a reader will query:
 *
 *  - street lighting lives under `landscaping` (obodonlashtirish), which is the
 *    administrative unit that owns it here, rather than as a 13th code;
 *  - `documents` is the bureaucratic request family (a `ma'lumotnoma`, a
 *    certificate, a registration), which is a large real share of the intake
 *    and would otherwise land in `other` and make that bucket meaningless.
 *
 * `other` is a first-class code, never a dumping ground to be hidden: an appeal
 * the set does not describe must be VISIBLY undescribed, for the same reason
 * §14.3.4 reports `unclassified` rather than redistributing it.
 */
export const AI_TOPICS = [
    'roads',
    'water',
    'electricity',
    'gas',
    'waste',
    'housing',
    'landscaping',
    'transport',
    'social',
    'documents',
    'noise_order',
    'other',
];
/** §14.4.1 — at most three topics per appeal; more is a list, not a triage aid. */
export const AI_TOPICS_MAX = 3;
export function isAiTopic(value) {
    return AI_TOPICS.includes(value);
}
/**
 * §14.4.2 `murojaat_ai.severity_flags` — a CLOSED flag set, **never free text**.
 *
 * These are the content-derived facts that justify a severity number, and they
 * exist so the number is never the only thing on screen: §14.4.5 rule 4 forbids
 * rendering a bare score, so the panel shows the flags that produced it.
 *
 * `repeat_complaint` is the one that needs its boundary stated. It means THE
 * TEXT SAYS this has been raised before — a claim by the citizen, unverified
 * like everything else here. It is NOT the repeat-appellant linkage, which is
 * SQL over `phone_hmac` (§14.4.1), carries no model at all, and is the number
 * the dashboard KPI reports. Conflating the two would put a model's reading of
 * a sentence into a figure the database can currently defend.
 */
export const AI_SEVERITY_FLAGS = [
    /** The text states a risk to health or life. */
    'health_risk',
    /** A physical hazard to the public: a collapsing structure, an open pit, a live cable. */
    'safety_hazard',
    /** An essential utility (water, gas, electricity, heat) is stated to be out NOW. */
    'utility_outage',
    /** A minor is named as affected. */
    'child_involved',
    /** An elderly, disabled or pregnant person is named as affected. */
    'vulnerable_person',
    /** The text states a street, block or whole mahalla is affected, not one household. */
    'many_affected',
    /** The text states the matter was raised before without result. NOT the SQL linkage. */
    'repeat_complaint',
    /** The text alleges misconduct by an official. Raises severity; decides nothing. */
    'official_misconduct',
];
export function isAiSeverityFlag(value) {
    return AI_SEVERITY_FLAGS.includes(value);
}
/**
 * §14.4.1 — the risk score is DECOMPOSED, and these are its two halves.
 *
 * SQL contributes 0–60 (deadline proximity 25, repeat history 15, status age
 * 10, assignee backlog 10); the model contributes 0–40 and nothing else. The
 * constants live here because the panel renders the two as separately-labelled
 * bars (§14.4.5 rule 4) and must scale each against its OWN maximum — drawing a
 * severity of 40 against a 100-point axis would show the model's ceiling as
 * "medium" and understate the one component a reader should discount.
 */
export const AI_SEVERITY_MAX = 40;
export const SQL_RISK_MAX = 60;
export const SQL_RISK_WEIGHTS = {
    /** How close `coalesce(extended_due_at, due_at)` is, or how far past. */
    deadline: 25,
    /** Prior appeals from the same `phone_hmac` inside the repeat window. */
    repeat: 15,
    /** How long the appeal has sat in its current status. */
    statusAge: 10,
    /** The assignee's open backlog — an appeal behind forty others is at risk. */
    backlog: 10,
};
/** §14.4.2 — the `murojaat_ai.status` state machine, mirrored from `transcript_status`. */
export const AI_STATUSES = ['pending', 'processing', 'done', 'failed', 'disabled'];
/**
 * §14.4.1 — the deadline-risk buckets, and they are **pure SQL**.
 *
 * Named in the domain rather than inside the query because the panel must
 * label this list `admin.ai.sqlBasis` — "computed from the database, no
 * artificial intelligence involved" — and the two have to agree about what the
 * buckets are. Calling SQL "AI" is the cheapest lie available on that screen
 * and §14.4.5 rule 6 forbids it.
 */
export const DEADLINE_RISK_BUCKETS = ['overdue', 'critical', 'warning', 'ok'];
/**
 * `critical` ≤ 1 day to the deadline, `warning` ≤ 3. Past it is `overdue`.
 *
 * RESCALED TO THE 5-DAY WINDOW (owner ruling, 2026-09-28). These were 2 and 5,
 * sized for F-6's 15-day window. Against a `due_at` of `received_at` +
 * `RESPONSE_DAYS` they stopped discriminating: an appeal arrives with exactly 5
 * days left, so `warning` fired on arrival, every open appeal was amber, and
 * `ok` became unreachable for anything without a lawful extension. A board that
 * is amber the moment an appeal lands tells an operator nothing.
 *
 * 3 and 1 restore the three-step reading inside a 5-day life: two days of `ok`,
 * then `warning`, then the final day `critical`. The labels
 * `admin.ai.bucket.warning` and `admin.ai.bucket.critical` name these numbers
 * out loud, so changing either constant means changing that copy in all four
 * locales.
 */
export const DEADLINE_CRITICAL_DAYS = 1;
export const DEADLINE_WARNING_DAYS = 3;
/**
 * §14.4.1 — the anomaly kinds, also **pure SQL/statistical**, also a closed set.
 *
 * Each is computed on read from `murojaats` (and, for `channel_silent`, from
 * `devices.last_heartbeat_at`, because a Telegram channel going quiet and a
 * kiosk being unplugged are different incidents and the panel must not report
 * the second as the first).
 */
export const ANOMALY_KINDS = [
    /** A mahalla's weekly volume above the trailing 8-week mean by > 2σ and ≥ 5. */
    'volume_spike',
    /** One topic's share of a mahalla's appeals jumped. */
    'topic_share_jump',
    /** A channel has produced nothing for 7 days. */
    'channel_silent',
    /** The overdue rate rose against the trailing mean. */
    'overdue_rate_jump',
];
/** §14.4.1 — a spike is > 2σ AND ≥ 5 appeals absolute. Both, so a quiet mahalla
 *  going from 1 to 3 is not an incident. */
export const ANOMALY_SIGMA = 2;
export const ANOMALY_MIN_ABSOLUTE = 5;
export const ANOMALY_TRAILING_WEEKS = 8;
/** §14.4.1 — a channel silent for this long is reported. */
export const ANOMALY_SILENT_DAYS = 7;
/**
 * §14.6 — every `action` string this platform writes to `audit_log`.
 *
 * Tarix renders a CLOSED dropdown built from this list, each entry with its own
 * `admin.audit.action.*` label. It is a domain constant rather than a screen's
 * array because the writers are spread across the API, the worker and the bot:
 * a new action added in a route and not added here would silently become an
 * unlabelled row in the one screen whose job is to account for staff activity.
 *
 * An action NOT in this list still renders — as its own raw string (§14.6) —
 * because a blank cell in an audit log is worse than an untranslated one.
 */
export const AUDIT_ACTIONS = [
    // admin session + search
    'admin.login',
    'admin.login.failed',
    'admin.search',
    // appeals
    'murojaat.submit',
    'murojaat.read',
    'murojaat.status.change',
    'murojaat.legal_type.set',
    'murojaat.retention_hold.change',
    'murojaat.retention.delete',
    'murojaat.transcript.done',
    'murojaat.transcript.failed',
    /**
     * §14.5.4 step 4 — the reception attestation.
     *
     * The staff member's statement that they read the appeal back to the citizen,
     * who confirmed it and consented, is evidenced by THIS ROW plus
     * `murojaats.filed_by` — never by a free-text field, which is where a
     * citizen's name leaks into a log §10.4 requires to stay PII-free.
     */
    'murojaat.reception.filed',
    /**
     * §14.4 — the analysis job's two terminal outcomes.
     *
     * They exist for a reason the transcription precedent only half explains. The
     * detail carries a model name, a provider name and a status code and NEVER a
     * topic, a flag or a score (§14.4.5 rule 3) — so what the row records is not
     * the analysis but the FACT OF IT: that this appeal's text was sent to a
     * named processor on a named date. Under F-8 that is precisely the fact
     * somebody may later have to be able to prove, per appeal.
     */
    'murojaat.ai.analyzed',
    'murojaat.ai.failed',
    'media.stream',
    'media.infected',
    'media.retention.delete',
    // §14.2.6 — reading one person's whole appeal history
    'appellant.read',
    // directory
    'mahalla.create',
    'mahalla.update',
    'mahalla.deactivate',
    'yettiligi.create',
    'yettiligi.update',
    'yettiligi.deactivate',
    'yettiligi.photo.upload',
    // fleet
    'device.create',
    'device.update',
    'device.enroll',
    'device.enroll_code.issue',
    // staff accounts
    'admin_user.create',
    'admin_user.update',
    'admin_user.totp.reset',
];
export const HOUR_S = 3600;
export const DAY_S = 86_400;
export const WEEK_S = 604_800;
export const MINUTE_S = 60;
export const RATE_LIMITS = {
    submitPerDeviceHour: { bucket: 'submit:device', limit: 10, windowSeconds: HOUR_S },
    submitPerDeviceDay: { bucket: 'submit:device', limit: 40, windowSeconds: DAY_S },
    submitPerInstallDay: { bucket: 'submit:install', limit: 5, windowSeconds: DAY_S },
    submitPerTelegramUserDay: { bucket: 'submit:tg', limit: 5, windowSeconds: DAY_S },
    submitPerPhoneDay: { bucket: 'submit:phone', limit: 5, windowSeconds: DAY_S },
    submitPerPhoneWeek: { bucket: 'submit:phone', limit: 15, windowSeconds: WEEK_S },
    submitPerIpHour: { bucket: 'submit:ip', limit: 30, windowSeconds: HOUR_S },
    statusPerIpHour: { bucket: 'status:ip', limit: 20, windowSeconds: HOUR_S },
    statusPerReferenceHour: { bucket: 'status:ref', limit: 10, windowSeconds: HOUR_S },
    installsPerIpDay: { bucket: 'install:ip', limit: 5, windowSeconds: DAY_S },
    loginPerIpHour: { bucket: 'login:ip', limit: 10, windowSeconds: HOUR_S },
    publicGetPerIpMinute: { bucket: 'get:ip', limit: 120, windowSeconds: MINUTE_S },
    /**
     * §12M.3.4 — the Mini App's buckets. `botService` is exempt from the IP
     * buckets because every bot request arrives from one container IP; Mini App
     * requests arrive from CITIZENS' OWN IPs through Caddy, so the IP buckets are
     * meaningful again and that exemption must never be extended to `nvw_`.
     */
    statusPerTelegramUserHour: { bucket: 'status:tg', limit: 10, windowSeconds: HOUR_S },
    webappSessionPerTelegramUserHour: { bucket: 'wa:session:tg', limit: 30, windowSeconds: HOUR_S },
    webappSessionPerIpHour: { bucket: 'wa:session:ip', limit: 60, windowSeconds: HOUR_S },
    webappPhonePerTelegramUserHour: { bucket: 'wa:phone:tg', limit: 5, windowSeconds: HOUR_S },
    webappProfilePerTelegramUserHour: { bucket: 'wa:profile:tg', limit: 60, windowSeconds: HOUR_S },
    /**
     * §14.5.5 — the ONLY bucket a staff-filed reception appeal is subject to.
     *
     * It is EXEMPT from `submit:ip`, `submit:phone` and the device buckets, and
     * that exemption is reasoned rather than convenient: those exist to police
     * UNAUTHENTICATED self-service, and this path is authenticated, audited and
     * capability-gated. Leaving `submit:phone` (5/day) in place would refuse the
     * fourth member of one family filing at one desk — a real reception day, told
     * it is abuse.
     *
     * What replaces it bounds the thing actually worth bounding: a compromised
     * staff account. 60/day is far above any real desk's throughput and far below
     * what an automated abuse of a stolen session would want.
     */
    submitPerAdminDay: { bucket: 'submit:admin', limit: 60, windowSeconds: DAY_S },
    /**
     * §14.4.3 — the analysis job's DAILY CEILING on calls to the foreign
     * processor, counted in the same `rate_events` table under a fixed key so it
     * inherits the nightly GC and adds no schema.
     *
     * It is not an abuse control; it is a bill and a quota control. D-043 records
     * that staging shares one Vertex service account and its quota with another
     * project, so a `prompt_version` bump re-enqueueing a five-year backlog is
     * exactly the event this stops. The limit is overridable per deployment
     * (`AI_ANALYZE_MAX_CALLS_PER_DAY`); the bucket key is not.
     */
    aiAnalyzePerDay: { bucket: 'ai:analyze:day', limit: 500, windowSeconds: DAY_S },
};
/** §5 — per account: lock 15 min after 5 failures. */
export const LOGIN_MAX_FAILURES = 5;
export const LOGIN_LOCK_MINUTES = 15;
// ---------------------------------------------------------------------------
// §7.2 / D-032 §4.0 — admin authorization is CAPABILITIES, NOT RANK.
//
// `viewer < handler < content_editor < admin` read as a linear hierarchy would
// give a content editor — whose job is the mahalla directory — read access to
// citizens' phone numbers, addresses and appeal bodies, purely because
// `content_editor` is declared after `handler` in the enum. That is a PII
// over-exposure bug, so no code anywhere may compare roles by index.
// ---------------------------------------------------------------------------
export const ADMIN_CAPABILITIES = {
    'inbox.list': ['viewer', 'handler', 'admin'],
    'appeal.read': ['handler', 'admin'], // full record + PII, audit-logged
    'appeal.media': ['handler', 'admin'],
    'appeal.transition': ['handler', 'admin'],
    'appeal.reopen': ['admin'],
    'appeal.retention_hold': ['admin'],
    'directory.read': ['viewer', 'handler', 'content_editor', 'admin'],
    /**
     * The staff directory — names, roles, photographs, office numbers.
     *
     * `hr` ADDED 2026-08-19, and it is a grant worth reading twice: this
     * capability writes the CITIZEN-FACING yettiligi card, not an internal
     * record. A name changed here changes what a citizen reads on a kiosk in
     * their mahalla.
     *
     * It is right anyway, because of D-MBT-14: attendance attaches to
     * `yettiligi_members` and there IS no second staff table. The alternative was
     * a parallel MBT-only endpoint writing the same rows, which would mean two
     * routes, two audit actions and two validation paths for one fact — and the
     * first time they disagreed, the citizen card and the attendance board would
     * show different people. The owner asked HR to be able to add employees;
     * adding an employee IS writing this row.
     *
     * `governor` is deliberately NOT here. Typing somebody's name and phone
     * number into a public directory is clerical work, and §8.2's argument for
     * keeping `mbt.staff.manage` off the governor applies unchanged.
     */
    'yettiligi.write': ['content_editor', 'hr', 'admin'],
    'mahalla.write': ['admin'],
    'device.read': ['handler', 'admin'],
    'device.write': ['admin'],
    'user.manage': ['admin'],
    /**
     * §14.1 (D-044) — Admin Panel V2 analytics.
     *
     * `viewer` gains `analytics.read` and NOTHING else: counts and charts, never
     * an appellant row, never a body, never an AI field. That is the whole reason
     * the aggregates live on their own capability instead of riding on
     * `inbox.list` — a number derived from citizens' appeals is a different
     * disclosure from the appeals themselves, and a role that may see the first
     * must not automatically see the second.
     *
     * `content_editor` gains neither. Its job is still the mahalla directory.
     */
    'analytics.read': ['viewer', 'handler', 'admin'],
    /**
     * §14.1 — `?format=csv` on the stats endpoints, `admin` only.
     *
     * Separate from `analytics.read` because a CSV leaves the building. §14.10.8
     * scopes it to AGGREGATES only: a citizen-level export (names, phones,
     * bodies) is a PII exfiltration channel with no per-row audit, and if it is
     * ever needed it is a different endpoint with its own named legal basis.
     */
    'analytics.export': ['admin'],
    /**
     * §14.1 (D-044 round B) — per-staff workload metrics, `admin` ONLY and
     * org-wide.
     *
     * Deliberately not `handler`: a screen listing every colleague's open count,
     * overdue count and median days-to-answer, visible to the colleagues
     * themselves, is a peer-ranking board rather than a management view. §14.2.5
     * additionally refuses to compute a composite score from these components —
     * an appeal's difficulty is not in this dataset, so a single ranking number
     * would be an HR instrument built from a proxy metric.
     */
    'analytics.staff': ['admin'],
    /**
     * §14.2.6 — the appellants registry: people who have filed, grouped by
     * `phone_hmac`. `handler` and `admin`, never `viewer`.
     *
     * §14.1's ruling in one line: a `viewer` may see HOW MANY people have come
     * back (`kpi.repeat_appellants`, an aggregate) and may never see WHO they
     * are. The list row carries a name and a phone tail, which is an appellant
     * row, which is the disclosure `analytics.read` deliberately stops short of.
     */
    'appellant.list': ['handler', 'admin'],
    /**
     * §14.2.6 — one person's whole appeal history, and it is AUDIT-LOGGED.
     *
     * Separate from `appellant.list` for the same reason `appeal.read` is
     * separate from `inbox.list`: listing every appeal one citizen has ever filed
     * is the read that must be attributable to a named staff member.
     */
    'appellant.read': ['handler', 'admin'],
    /**
     * §14.3 — set `murojaats.legal_type`, the Art. 6 statutory classification.
     *
     * Held by exactly the roles that already open appeals, because the
     * classification is made on an appeal the staff member is reading anyway.
     * `viewer` never gets it: reading a chart is not making a legal
     * determination.
     */
    'appeal.classify': ['handler', 'admin'],
    /**
     * §14.6 — Tarix, the audit log made visible. `admin` only, ORG-WIDE.
     *
     * Deliberately absent from `MAHALLA_SCOPED_CAPABILITIES` below: an audit
     * trail restricted by scope cannot answer "who read this record", which is
     * the only question it exists to answer. Read-only by construction — there is
     * no write, edit or delete route for `audit_log`, in §14 or ever.
     */
    'audit.read': ['admin'],
    /**
     * §14.5 — file an appeal on behalf of a citizen standing at the qabulxona.
     *
     * `handler` and `admin`, mahalla-scoped, and deliberately NOT `viewer` or
     * `content_editor`: this capability CREATES a legal record in a citizen's
     * name, on the staff member's attestation that they read it back and the
     * citizen consented (§14.5.4). It is the only write in the platform that
     * originates a `murojaats` row from inside the building, which is why it is
     * its own capability rather than an extension of `appeal.transition`.
     */
    'appeal.create': ['handler', 'admin'],
    /**
     * §14.4 — the AI block on the detail screen and the AI board.
     *
     * `handler` and `admin`, never `viewer`, and it is a SEPARATE capability from
     * `appeal.read` on purpose: every `murojaat_ai` field is derived from `body`
     * or `transcript_text` (§14.4.2) and is governed identically, but a
     * deployment that wants staff to read appeals WITHOUT machine opinions on
     * them must be able to say so by withholding one capability rather than by
     * turning the provider off for everybody.
     *
     * Read-only, always. There is no `ai.write`: §14.4.5 rule 2 says the model
     * writes to `murojaat_ai` and to nothing else, and no staff capability
     * exists that would let a person write into it either.
     */
    'ai.read': ['handler', 'admin'],
    // -------------------------------------------------------------------------
    // MBT — Markaziy boshqaruv tizimi (MBT-project-overview.md §1.3, §17 P0).
    //
    // Every one of these is NEW. Not one line above this comment changed when
    // they were added, which is the mechanical form of "MBT is additive": the
    // murojaat panel's authorization is exactly what it was, and the six MBT
    // roles hold no murojaat capability at all.
    //
    // `admin` appears in each list because it is this platform's superuser and
    // somebody has to be able to administer MBT on the day it is installed —
    // not because rank confers capability (it does not; see §7.2 above).
    // -------------------------------------------------------------------------
    /**
     * May open `apps/command` at all. The coarse door, checked once by the shell;
     * every screen behind it still checks its own capability.
     *
     * Held by all six MBT roles, so `mahalla_lead` can reach the panel and then
     * be scoped down to their own mahalla by the capabilities below rather than
     * by being refused at the door.
     */
    'mbt.access': ['governor', 'operator', 'technician', 'hr', 'mahalla_lead', 'content', 'admin'],
    /**
     * §15.7's city line and four figures: kiosks working, staff present, appeals
     * today, open alerts.
     *
     * Aggregates only — no appellant row, no body, no name — which is why a
     * `content` manager and an `hr` officer may hold it without holding anything
     * from the murojaat side. It is deliberately NOT `analytics.read`: that
     * capability governs the murojaat panel's own analytics screens, and granting
     * it to a governor to satisfy one number would hand them the appellant-facing
     * surfaces behind it too.
     */
    'mbt.overview.read': [
        'governor',
        'operator',
        'technician',
        'hr',
        'mahalla_lead',
        'content',
        'admin',
    ],
    /**
     * §4 — fleet state: the grid, kiosk detail, telemetry history, alerts.
     *
     * Mahalla-scoped below, which is what makes `mahalla_lead` safe to include:
     * they see their own kiosk and no other. `hr` and `content` are absent —
     * neither has a reason to read a kiosk's CPU temperature.
     */
    'mbt.fleet.read': ['governor', 'operator', 'technician', 'mahalla_lead', 'admin'],
    /**
     * §4.5 — issue a remote command to a kiosk (restart, reboot, lock, update…).
     *
     * OWNER DIRECTIVE (2026-08-17), answering open question 3: remote-control
     * rights are governor + operator only. A `technician` therefore reads fleet
     * state and works the alert queue but does not reach for the machine itself
     * in P0 — if that proves wrong in practice it is one entry in this list, and
     * it is far easier to add a role here later than to explain a reboot nobody
     * meant to authorise.
     */
    'mbt.command.issue': ['governor', 'operator', 'admin'],
    /**
     * §5 — open a live camera or screen view of a kiosk.
     *
     * Same owner directive as `mbt.command.issue`, and the same list: watching a
     * public space is at least as consequential as restarting a machine. §3.3
     * governs the rest of it — every session is visible on the kiosk itself and
     * written to an immutable audit row.
     */
    'mbt.live.view': ['governor', 'operator', 'admin'],
    /**
     * §5.3 — LIVE AUDIO FROM A KIOSK MICROPHONE. Held by NO ROLE. That empty list
     * is the feature flag, and it is deliberate.
     *
     * OWNER DIRECTIVE (2026-08-17), answering open question 2: build it, ship it
     * disabled behind a capability. Listening to a public building raises the
     * legal bar sharply (§18 risk 2) and the answer to "is it needed at all" is
     * still open, so the code gets written and tested while the authority to
     * exercise it does not exist anywhere in the system.
     *
     * `can(role, 'mbt.live.audio')` is false for every role including `admin`, so
     * enabling it is a reviewed code change that names a role in this list — not
     * a checkbox somebody ticks at 2 a.m., and not a config value that differs
     * between two deployments without anyone noticing.
     */
    'mbt.live.audio': [],
    /**
     * §7 — the attendance board, an employee's own history, the timesheet.
     *
     * `hr` is the role this exists for (§1.3: "attendance boards, timesheets,
     * leave approvals, staff records, face enrollment"), and `mahalla_lead` holds
     * it scoped to their own mahalla — a lead needs to know who is in their
     * building and has no business reading another mahalla's staff.
     *
     * `operator` is ABSENT. The console watches machines; a person's working day
     * is not fleet state, and giving the fleet console attendance because both
     * happen to be "MBT" is how a monitoring tool quietly becomes surveillance of
     * colleagues.
     */
    'mbt.attendance.read': ['governor', 'hr', 'mahalla_lead', 'admin'],
    /**
     * §7.10 — MANUAL CORRECTION. Rewriting somebody's working day.
     *
     * Narrower than reading it, and narrower than §1.3's list would suggest:
     * `mahalla_lead` reads their mahalla's board and cannot edit it. A lead
     * correcting their own staff's attendance is precisely the conflict of
     * interest that makes a record worthless — the person judged by the numbers
     * must not be the person who can change them.
     *
     * Corrections are append-only rows carrying a reason and an actor
     * (`attendance_events_correction_attributed`), so this capability grants the
     * ability to ADD a correction, never to erase what was there.
     */
    'mbt.attendance.correct': ['hr', 'admin'],
    /**
     * §8.2/§8.3 — staff records, PIN issue and reset, consent, face enrollment.
     *
     * `hr` and `admin` only. NOT the governor: signing somebody up for biometric
     * capture is clerical work with a consent form attached, and the person who
     * can authorise it should be the person who holds the form. A governor who
     * needs it grants themselves `hr`, and that grant is an audit row.
     */
    'mbt.staff.manage': ['hr', 'admin'],
    /**
     * §6.4/§6.5 — PUT WORDS ON EVERY PUBLIC SCREEN IN THE CITY.
     *
     * `governor`, `content` and `admin`. §1.3 gives `content` the announcements
     * and ad playlist, and a governor must be able to reach the «Jonli eʼlon»
     * composer without going through anybody — §6.5's sixty seconds does not
     * survive a phone call.
     *
     * `operator` is ABSENT and that is the uncomfortable one, because the
     * operator is who is at the console at 3 a.m. §6.3 rules that a civic notice
     * may skip commercial review but "still record an approver", and the smallest
     * honest reading of that is that broadcasting to the province is not the same
     * authority as restarting a kiosk. If the night shift needs it, that is one
     * entry in this list and an owner's decision — not something to assume.
     */
    'mbt.broadcast.publish': ['governor', 'content', 'admin'],
    /**
     * §6.2 — booking a paid slot on a government screen.
     *
     * DELIBERATELY NARROWER THAN BROADCASTING, and not for the obvious reason. An
     * announcement is urgent and reversible: the wrong one is taken down in a
     * click and the harm is an hour of confusion. An ad is neither — it is a
     * commercial arrangement with somebody outside the building, it runs for
     * weeks, and the question it raises (§18 risk 5) is who was paid.
     *
     * `content` can broadcast an emergency at 3 a.m. but cannot sell a slot. That
     * asymmetry is the point: the capability that needs speed and the capability
     * that needs accountability are not the same capability.
     */
    'mbt.ads.manage': ['governor', 'admin'],
};
export function can(role, capability) {
    return ADMIN_CAPABILITIES[capability].includes(role);
}
/**
 * §4.0 — `mahalla_scope` filters `inbox.list`, `appeal.*` and `device.read`.
 * It never applies to `directory.*` (public data) or to `user.manage`.
 */
export const MAHALLA_SCOPED_CAPABILITIES = [
    'inbox.list',
    'appeal.read',
    'appeal.media',
    'appeal.transition',
    'appeal.reopen',
    'appeal.retention_hold',
    'device.read',
    /**
     * §14.1 — a `handler` with a `mahalla_scope` sees analytics for their scope
     * only, and every scoped aggregate response carries `"scoped": true` so the
     * panel can label it. An unlabelled scoped count is a lie: a handler reading
     * `total: 42` must not take it for the city's total.
     *
     * `analytics.export` is deliberately NOT here — it is `admin` only, and
     * `admin` never carries a scope.
     */
    'analytics.read',
    /**
     * §14.1 round B. A scoped handler's appellant registry only sees appeals
     * filed into their mahallas, which makes `appeal_count` scope-relative —
     * hence the `scoped: true` label §14.2.6 requires on the response. Without
     * that label the number is simply wrong.
     *
     * `analytics.staff` and `audit.read` are deliberately ABSENT from this list.
     * Both are `admin`-only and org-wide by ruling: a per-staff metric filtered
     * to one mahalla would misreport a colleague's workload, and a scoped audit
     * trail cannot answer "who read this".
     */
    'appellant.list',
    'appellant.read',
    'appeal.classify',
    /**
     * §14.1 round C. `appeal.create` is scoped because a reception appeal is
     * filed INTO a mahalla, and a handler who covers two mahallas must not be
     * able to originate a record in a third — `assertInScope(principal,
     * payload.mahalla_id)` is the enforcement, on the route.
     *
     * `ai.read` is scoped for the same reason `appeal.read` is: the AI block is
     * derived from one citizen's appeal text, so it inherits that appeal's scope
     * exactly.
     */
    'appeal.create',
    'ai.read',
    /**
     * MBT §15.7 "Mahalla lead — their mahalla only". Both capabilities are held
     * by `mahalla_lead`, and both must narrow to that person's own mahalla or the
     * role's entire premise is broken — a mahalla lead reading the whole city's
     * fleet grid is just an operator with a different label.
     *
     * `mbt.command.issue` and `mbt.live.view` are deliberately ABSENT: neither is
     * held by `mahalla_lead` at all (see above), so there is no scope to narrow.
     * `mbt.access` is absent for the same reason it would be meaningless — it is
     * the door, not a query.
     */
    'mbt.overview.read',
    'mbt.fleet.read',
    /**
     * §7 — a `mahalla_lead` reads THEIR mahalla's attendance board. Unscoped it
     * would hand a lead every staff member's working day in the province, which
     * is a different feature from the one §15.7 describes and one nobody
     * authorised.
     *
     * `mbt.attendance.correct` and `mbt.staff.manage` are absent because
     * `mahalla_lead` does not hold them at all — there is no scope to narrow.
     */
    'mbt.attendance.read',
];
export function isMahallaScoped(capability) {
    return MAHALLA_SCOPED_CAPABILITIES.includes(capability);
}
/**
 * MBT §15.7 — the role-adaptive home. "The same application opens differently
 * depending on who signs in… the difference between a system a governor admires
 * and one he avoids."
 *
 * A ROUTE PER ROLE, not a capability lookup, and that is the point. §15.7 is not
 * describing an access rule — an operator is perfectly *permitted* to read the
 * executive home — it is describing where each person's work begins. A governor
 * who lands in a cockpit has been failed by the software even though every
 * capability check passed.
 *
 * `admin` is absent deliberately: it holds every MBT capability but is a
 * platform administrator rather than an MBT persona, so it lands on the
 * operator console, which is the screen that shows the most and assumes the
 * least.
 */
export const MBT_HOME_ROUTE = {
    governor: '/',
    operator: '/console',
    technician: '/queue',
    hr: '/davomat',
    mahalla_lead: '/mahalla',
    content: '/broadcast',
};
/** The route an authenticated MBT session should be sent to on entry. */
export function mbtHomeRoute(role) {
    return isMbtRole(role) ? MBT_HOME_ROUTE[role] : '/console';
}
// ---------------------------------------------------------------------------
// MBT §4.3 — the kiosk status model.
//
// Shared here rather than implemented in the API because THREE processes need
// the same answer: the API computes it for the fleet grid, `services/realtime`
// computes it when presence changes, and `apps/command` recomputes it locally
// between polls so a tile can age from «Sogʻlom» to «Oflayn» on a screen nobody
// has refreshed. Three implementations of one threshold is three chances for a
// kiosk to be green in one place and red in another.
// ---------------------------------------------------------------------------
/** §4.3 — a kiosk is EXACTLY ONE of these. */
export const KIOSK_STATUSES = ['healthy', 'warning', 'critical', 'offline', 'maintenance'];
/**
 * §4.2's cadence is 30 s and §4.3's healthy bound is 90 s — three missed
 * reports, not one. A single dropped packet on a mahalla's ADSL line is not an
 * incident, and an alert that fires on one is an alert operators learn to
 * ignore.
 */
export const KIOSK_HEARTBEAT_STALE_S = 90;
/**
 * §4.3/§4.4 — no telemetry for 15 minutes is `offline`, and `kiosk_offline` is
 * the critical alert that goes with it. Between 90 s and 15 min the kiosk is
 * `critical`: something is wrong, but a Windows update reboot lands in that
 * window and does not deserve a technician being dispatched.
 */
export const KIOSK_OFFLINE_S = 15 * 60;
/**
 * §4.3, in precedence order — and the order is the whole content of this
 * function.
 *
 * `maintenance` outranks everything because a kiosk somebody deliberately took
 * out of service is not a fault, and letting it also show as `offline` is how a
 * planned window generates the alert it was scheduled to prevent.
 *
 * `offline` outranks `critical` because "we cannot see it" is a more useful
 * thing to tell an operator than any stale detail about what was wrong when we
 * last could.
 *
 * A kiosk that has NEVER reported is `offline`, not `healthy`. The absence of
 * bad news is not good news — §3.5 in one line.
 */
export function deriveKioskStatus(input) {
    const now = input.now ?? new Date();
    if (input.maintenanceUntil && input.maintenanceUntil.getTime() > now.getTime()) {
        return 'maintenance';
    }
    if (input.telemetryAt === null)
        return 'offline';
    const ageS = (now.getTime() - input.telemetryAt.getTime()) / 1000;
    if (ageS > KIOSK_OFFLINE_S)
        return 'offline';
    if (input.hasCriticalAlert)
        return 'critical';
    if (ageS > KIOSK_HEARTBEAT_STALE_S)
        return 'critical';
    if (input.hasWarningAlert)
        return 'warning';
    return 'healthy';
}
// ---------------------------------------------------------------------------
// MBT §4.5 — the remote-command vocabulary.
//
// SHARED because three parties must agree on it and disagree dangerously: the
// API decides what a caller may issue, the KIOSK decides what it will execute
// and what it must defer, and the PANEL decides what to warn about before the
// operator presses anything. Three copies of "is this disruptive" is three
// chances for a restart to land mid-appeal.
// ---------------------------------------------------------------------------
/** §4.5's verbs. */
export const KIOSK_COMMANDS = [
    'app.restart',
    'machine.reboot',
    'kiosk.lock',
    'kiosk.unlock',
    'update.install',
    'update.rollback',
    'cache.clear',
    'content.refresh',
    'screenshot.capture',
    'logs.pull',
    'diagnostics.run',
    'locale.set',
    'volume.set',
    'brightness.set',
    'device.reenroll',
    'attendance.sync',
    'config.set',
];
/**
 * §3.1 AS AMENDED BY D-MBT-10 — the commands the appeal lock defers.
 *
 * §3.1 is the highest-priority rule in the system and cuts live viewing,
 * suppresses broadcast audio and blocks takeovers while a citizen records a
 * murojaat. It does not mention commands, and that is a gap rather than a
 * decision: `app.restart` during a capture destroys that citizen's appeal, and
 * the citizen has no idea why the screen went black.
 *
 * Two different reasons put a command on this list, and both matter:
 *
 *   IT WOULD DESTROY THE RECORDING — `app.restart`, `machine.reboot`,
 *   `update.install`, `update.rollback`, `device.reenroll`, `cache.clear`,
 *   `kiosk.lock`. The appeal is lost and cannot be re-made; the citizen has
 *   already said what they came to say.
 *
 *   IT WOULD *VIEW* THE CAPTURE — `screenshot.capture`. §3.1 cuts screen
 *   viewing during capture, and a screenshot is a view. It is deferred rather
 *   than refused because a screenshot taken later, of the attract screen, is
 *   exactly as useful and carries none of the citizen's session.
 *
 *   IT WOULD CHANGE THE UI MID-SENTENCE — `locale.set`. Not destructive, but a
 *   citizen whose language changes while they are speaking has been failed.
 *
 * DEFERRED, NEVER FAILED. A failure teaches an operator to re-issue, and
 * re-issuing is precisely the behaviour that eventually lands one mid-appeal.
 */
export const APPEAL_LOCK_DEFERRED_COMMANDS = [
    'app.restart',
    'machine.reboot',
    'kiosk.lock',
    'update.install',
    'update.rollback',
    'cache.clear',
    'screenshot.capture',
    'locale.set',
    'device.reenroll',
];
export function isDeferredDuringCapture(command) {
    return APPEAL_LOCK_DEFERRED_COMMANDS.includes(command);
}
/**
 * §4.5 — "dangerous commands require a typed confirmation and are rate-limited".
 *
 * The test is not "could this go wrong" — every command could. It is **can the
 * kiosk come back by itself?** A restart that hangs is fixed by the watchdog; a
 * reboot that does not come up needs somebody to drive to a mahalla, and a
 * `device.reenroll` that goes wrong takes the kiosk's credential with it.
 *
 * `update.rollback` is here because it downgrades a machine nobody is standing
 * next to, and `config.set` because it is the one verb whose blast radius is
 * whatever key was named.
 */
export const DANGEROUS_KIOSK_COMMANDS = [
    'machine.reboot',
    'device.reenroll',
    'update.rollback',
    'config.set',
];
export function isDangerousCommand(command) {
    return DANGEROUS_KIOSK_COMMANDS.includes(command);
}
// ---------------------------------------------------------------------------
// MBT §4.8 — auto-remediation playbooks.
//
// THIS IS THE ONE FEATURE IN MBT THAT ACTS RATHER THAN REPORTS. Everything else
// observes, asks, or waits for an operator; this restarts software on 35
// machines in public buildings with nobody in the loop. Every rule below is
// therefore stated as a constraint rather than a capability.
// ---------------------------------------------------------------------------
/**
 * The ONLY commands a playbook may ever issue. An ALLOW-LIST, deliberately —
 * an exclusion list grows a hole every time somebody adds a verb.
 *
 * Each of these is recoverable without a human: an app that will not restart is
 * still a running machine an operator can reach, a cleared cache refills, a
 * refreshed manifest re-downloads.
 */
export const PLAYBOOK_ALLOWED_COMMANDS = [
    'app.restart',
    'cache.clear',
    'content.refresh',
];
/**
 * FORBIDDEN TO AUTOMATION, ALWAYS, and each for its own reason. Listed
 * explicitly rather than merely omitted, so that adding one back is a decision
 * with a name on it rather than an edit nobody notices:
 *
 *   `update.install`   — a bad build auto-installed across the fleet is the
 *                        single worst thing this system could do. A staged
 *                        rollout (§4.6) exists precisely so a human watches
 *                        each stage.
 *   `machine.reboot`   — a kiosk that does not come back up is a site visit,
 *                        and the box is in a building that locks at 18:00.
 *   `device.reenroll`  — can orphan a kiosk from its mahalla, and the recovery
 *                        needs somebody physically present with a code.
 *   `update.rollback`  — downgrades an unattended machine; same argument as
 *                        install, in the other direction.
 *   `config.set`       — its blast radius is whatever key was named, which is
 *                        exactly the property automation must not have.
 */
export const PLAYBOOK_FORBIDDEN_COMMANDS = [
    'update.install',
    'update.rollback',
    'machine.reboot',
    'device.reenroll',
    'config.set',
];
export function isPlaybookAllowed(command) {
    return PLAYBOOK_ALLOWED_COMMANDS.includes(command);
}
/**
 * §4.8's playbooks: `crash_loop` → restart the app once, then escalate ·
 * `disk_low` → clear cache and prune media, then escalate · `queue_stuck` →
 * force a sync attempt · `content_stale` → re-pull the manifest.
 *
 * `no_usage` IS DELIBERATELY ABSENT AND MUST STAY ABSENT. A kiosk nobody
 * touched in 24 hours is almost always a placement or access problem —
 * furniture in front of it, a locked room, a dead wall display. Restarting it
 * repeatedly fixes nothing and buries the one signal that says a human needs to
 * walk over there. `no_usage` is §4.4's most valuable alert precisely because
 * it cannot be fixed remotely.
 *
 * `kiosk_offline`, `camera_lost`, `mic_lost`, `temp_high`, `tamper` and
 * `update_failed` are absent for the same family of reasons: none of them has a
 * remote fix, and pretending otherwise would consume the alert without
 * addressing the fault.
 */
export const PLAYBOOKS = [
    { rule: 'app_crash_loop', command: 'app.restart' },
    { rule: 'disk_low', command: 'cache.clear' },
    { rule: 'queue_stuck', command: 'content.refresh' },
];
/**
 * §4.8's circuit breaker: at most this many automatic actions per rule per
 * kiosk per 24 hours, after which the playbook DISABLES ITSELF for that kiosk
 * and escalates to a human.
 *
 * Without it, `crash_loop → restart` becomes restart→crash→restart forever, and
 * the alert that should have summoned an engineer is instead being papered over
 * every ninety seconds. Three is enough for a transient fault to clear and few
 * enough that a real one surfaces within the hour.
 */
export const PLAYBOOK_MAX_ACTIONS_PER_DAY = 3;
export const PLAYBOOK_WINDOW_HOURS = 24;
/** §4.4's rule name for a tripped breaker. Raised by the engine, like any other. */
export const AUTO_REMEDIATION_HALTED_RULE = 'auto_remediation_halted';
// ---------------------------------------------------------------------------
// MBT §4.8 — escalation chains.
//
// "Alert unacknowledged 15 min → supervisor; 30 min → duty manager; critical
// unresolved 2 h → governor's digest. Configurable per rule, with quiet hours."
//
// ESCALATION EXISTS TO CATCH ABANDONMENT, NOT TO PUNISH SOMEBODY MID-REPAIR.
// Every rule below follows from that one sentence.
// ---------------------------------------------------------------------------
export const ESCALATION_LEVELS = [0, 1, 2, 3];
/** §4.8's ladder, verbatim. Minutes from `opened_at`. */
export const ESCALATION_LADDER = [
    { level: 1, afterMinutes: 15, to: 'supervisor' },
    { level: 2, afterMinutes: 30, to: 'duty_manager' },
    { level: 3, afterMinutes: 120, to: 'governor_digest', criticalOnly: true },
];
/**
 * §4.8's quiet hours, Asia/Tashkent.
 *
 * 22:00–07:00. Inside them the chain does not ADVANCE — it does not wake a duty
 * manager at 02:00 for a warning that can wait until the morning shift.
 *
 * CRITICAL ALERTS IGNORE QUIET HOURS, and that exception is the point of having
 * two severities at all. A kiosk that has been offline since midnight is
 * something the morning shift can handle; a fleet-wide failure is not, and
 * "quiet hours" must never become a nine-hour window in which nothing can reach
 * anybody.
 */
export const QUIET_HOURS_START = 22;
export const QUIET_HOURS_END = 7;
export function isQuietHour(hourTashkent) {
    return hourTashkent >= QUIET_HOURS_START || hourTashkent < QUIET_HOURS_END;
}
/**
 * The step this alert should be escalated to NOW, or `null` for "leave it
 * alone". Three refusals, each of which matters more than the ladder itself:
 *
 *  1. AN ALERT BEING WORKED IS NEVER ESCALATED. Acknowledged AND assigned means
 *     somebody has it. Escalation exists to catch abandonment, and waking a
 *     duty manager at 02:00 because a timer expired while a technician was
 *     mid-repair is how a chain gets muted — after which it catches nothing.
 *
 *     ACKNOWLEDGED ALONE IS NOT ENOUGH. Acknowledging is a click; it says "I
 *     have seen this", not "I am doing something about it". An alert that has
 *     been seen and left is exactly the abandonment this catches.
 *
 *  2. THE CHAIN TERMINATES. `escalatedLevel` is the highest step already
 *     notified, and a step is never repeated. Reaching the top means stopping,
 *     not re-notifying every sixty seconds until somebody turns the whole
 *     mechanism off.
 *
 *  3. QUIET HOURS HOLD A WARNING, NEVER A CRITICAL. See `isQuietHour`.
 */
export function nextEscalation(input) {
    // 1. Somebody has it.
    if (input.acknowledgedAt !== null && input.assignedTo !== null)
        return null;
    // 2. Already at the top.
    if (input.escalatedLevel >= 3)
        return null;
    const now = input.now ?? new Date();
    const ageMinutes = (now.getTime() - input.openedAt.getTime()) / 60_000;
    // 3. Quiet hours hold a warning back; a critical goes through.
    if (input.severity !== 'critical' && isQuietHour(input.hourTashkent))
        return null;
    let due = null;
    for (const step of ESCALATION_LADDER) {
        if (step.level <= input.escalatedLevel)
            continue;
        if (ageMinutes < step.afterMinutes)
            continue;
        // §4.8's third step is "CRITICAL unresolved 2 h → governor's digest". A
        // warning never reaches the governor, however long it sits — his home is
        // §15.7's calm executive view, and a two-hour-old disk warning is not what
        // it is for.
        if ('criticalOnly' in step && step.criticalOnly && input.severity !== 'critical')
            continue;
        due = step.level;
    }
    return due;
}
// ---------------------------------------------------------------------------
// MBT §4.8 / §10 — BULK OPERATIONS AND THE TWO-PERSON RULE.
//
// "Select many kiosks → preview exactly what will happen to each → execute with
// a live progress list… Fleet-wide destructive actions additionally require the
// two-person rule (§10)."
//
// ONE OPERATOR, ONE CLICK, THIRTY-FIVE MACHINES. That is the whole risk, and
// every rule below narrows it.
// ---------------------------------------------------------------------------
/**
 * The commands a bulk plan may not execute on one person's authority.
 *
 * DELIBERATELY WIDER THAN `DANGEROUS_KIOSK_COMMANDS`, because the two lists
 * answer different questions.
 *
 *   Single-kiosk «dangerous» asks: CAN THE KIOSK COME BACK BY ITSELF? A restart
 *   that hangs is fixed by the watchdog; a reboot that does not come up needs
 *   somebody to drive to a mahalla.
 *
 *   Bulk «destructive» asks: IF THIS IS WRONG, HOW MUCH OF THE PROVINCE IS DOWN
 *   AT ONCE? Two verbs answer differently under that question:
 *
 *     `update.install` — a bad build reaching 35 kiosks simultaneously is the
 *     single worst thing this system can do. It is precisely the verb the
 *     playbook allow-list exists to keep automation away from, and a human
 *     doing it to the whole fleet unreviewed is the same outcome by hand.
 *
 *     `kiosk.lock` — trivially reversible per kiosk, and that is exactly the
 *     trap. Locking the fleet takes every kiosk in the province out of service
 *     at once, and un-locking them requires the same system that just locked
 *     them still working.
 *
 * `cache.clear` and `app.restart` are NOT here. They are disruptive and they are
 * recoverable, and a rule that demands a second person for a fleet-wide cache
 * clear is a rule operators will route around at 03:00 — after which the second
 * person is a habit nobody has when it matters.
 */
export const BULK_TWO_PERSON_COMMANDS = [
    ...DANGEROUS_KIOSK_COMMANDS,
    'update.install',
    'kiosk.lock',
];
/**
 * Whether a bulk plan for this command needs a second person.
 *
 * NOTE WHAT IS ABSENT: a target count. There is no threshold, because there is
 * no principled place to put one between two kiosks and ten, and a wrong
 * threshold is worse than none — it is a rule that looks enforced and is not.
 * A bulk route exists to act on many machines at once; a single kiosk has its
 * own gate (§4.5's typed short code and reason) and does not need this one.
 */
export function bulkRequiresTwoPerson(command) {
    return BULK_TWO_PERSON_COMMANDS.includes(command);
}
/**
 * THE TWO-PERSON RULE ITSELF.
 *
 * A second person is a DIFFERENT person. This is the whole rule, it is one
 * comparison, and it is the one thing that is worth stating in three places —
 * here, in the route, and as a CHECK constraint in migration 0030 — because an
 * approval flow that accepts the issuer's own second browser session is not a
 * two-person rule, it is a confirmation dialog with extra steps, and it fails
 * silently: every audit row shows an approver, and the approver is the actor.
 *
 * WHAT THIS CANNOT DEFEND AGAINST: one human holding two staff accounts. No
 * check at this layer can, and pretending otherwise would be worse than saying
 * so. That is an account-provisioning control, and it belongs in §10's audit.
 */
export function isValidSecondPerson(issuedBy, approvedBy) {
    return issuedBy !== approvedBy;
}
/**
 * How long a plan — and therefore an approval — stays good.
 *
 * An approval is a statement about a fleet somebody just looked at. Thirty
 * minutes later it is a statement about a fleet that may have three new alerts
 * and a kiosk in a maintenance window, and executing on it is executing on a
 * photograph. Short enough that the picture is still true; long enough to find
 * a colleague.
 */
export const BULK_PLAN_TTL_MINUTES = 30;
/** Why the preview says a selected kiosk will NOT be acted on. */
export const BULK_SKIP_REASONS = [
    'maintenance_window',
    'inactive',
    'already_outstanding',
    'out_of_scope',
];
// ---------------------------------------------------------------------------
// MBT §7 — DAVOMAT (attendance).
//
// §7.1 states the two design problems this module has to solve, and both are
// about people rather than about software:
//
//   IT MUST NOT CONFUSE CITIZENS. The kiosk stays a citizen kiosk. Entry is a
//   quiet «Xodim» control, the whole check-in is ≤ 5 seconds, and ANY CITIZEN
//   TOUCH CANCELS IT — the citizen always wins.
//
//   IT MUST EARN ITS PLACE. Check-in data becomes a citizen service: the
//   yettiligi card says «hozir ish joyida». A citizen learns whether their
//   hokim yordamchisi is in right now, which is what makes an attendance system
//   on a public terminal self-explanatory rather than sinister.
// ---------------------------------------------------------------------------
/**
 * §7.3's work modes.
 *
 * `field` IS THE WHOLE POINT OF HAVING MODES. Mahalla staff are supposed to be
 * out among residents — that is the job — and a system that records «mahalla
 * aylanish» as absence "will be hated and gamed within a week" (§7.3). Shipping
 * present/absent and adding modes later is not a smaller version of this
 * feature; it is a different and worse one, because by then everybody has
 * learned to check in and stay put.
 */
export const WORK_MODES = ['office', 'field', 'meeting', 'leave'];
export const ATTENDANCE_METHODS = ['face', 'pin', 'manual'];
export const ATTENDANCE_DIRECTIONS = ['in', 'out'];
/**
 * §7.2 — "the system infers direction from the open session (last event)".
 *
 * THE KIOSK NEVER SENDS THE DIRECTION, and this function is why. A kiosk that
 * has been offline since morning does not know whether somebody checked this
 * person out from another terminal, or whether HR corrected the morning's
 * entry. It knows one thing — that this person is standing in front of it — and
 * the server owns what that means.
 *
 * A kiosk-declared direction would also be trivially gameable: check out, check
 * in again, and the day looks continuous.
 */
export function inferDirection(lastDirection) {
    return lastDirection === 'in' ? 'out' : 'in';
}
/**
 * ============================================================================
 * §7.5 — A CHECK-IN NOBODY CLOSED DOES NOT MEAN SOMEBODY IS STILL AT WORK.
 * ============================================================================
 *
 * "Missing check-out auto-closes at shift end and is flagged."
 *
 * This is the bug that ships by default, and it is a lying one: somebody checks
 * in on Monday, forgets to check out, and the board shows them at their desk on
 * Friday night. The governor's «Ish joyida» headline counts them. The citizen
 * card tells a resident to walk over. Nothing errors, and the number is wrong in
 * the most flattering possible direction — which is exactly what
 * `MbtOverview`'s rule 3 exists to prevent.
 *
 * TWELVE HOURS. A working day plus generous slack, so a long day or a late
 * event never trips it, and no session survives overnight.
 *
 * AND IT IS DERIVED, NOT WRITTEN. §7.5 says "auto-closes", and the obvious
 * implementation writes a synthetic check-out row at shift end. THIS SYSTEM
 * WILL NOT DO THAT. §7.10's whole philosophy is that an attendance record is
 * evidence about a person: a check-out event nobody made is a fabricated fact,
 * and it would be indistinguishable in the log from one they did. So the
 * session is treated as closed for PRESENCE — which is a reading of the
 * evidence, not an addition to it — and flagged for HR, who close it properly
 * with §7.10's attributed correction.
 *
 * The difference matters the day somebody's timesheet is questioned.
 */
export const MAX_OPEN_SESSION_HOURS = 12;
export function isStaleCheckIn(at, now = new Date()) {
    if (at === null)
        return false;
    return now.getTime() - at.getTime() > MAX_OPEN_SESSION_HOURS * 3_600_000;
}
/**
 * §7.1's citizen-facing question — is this person at work right now?
 *
 * DERIVED FROM THE LAST EVENT, NEVER STORED, for exactly the reason
 * `deriveKioskStatus` is: a stored `is_present` flag and the events behind it
 * drift apart the first time a job dies mid-write, and the flag is what the
 * citizen sees.
 *
 * `leave` and `meeting` are NOT «ish joyida». A citizen told their hokim
 * yordamchisi is in, who then walks to the mahalla and finds an empty office,
 * has been lied to by this system — and that citizen is exactly who §7.1 says
 * this feature has to earn its place with. `field` is the same: the person is
 * working, and they are not there.
 */
export function isAtDesk(lastDirection, workMode, at = null, now = new Date()) {
    if (isStaleCheckIn(at, now))
        return false;
    return lastDirection === 'in' && workMode === 'office';
}
/**
 * The presence a citizen-facing card shows: at the desk, working elsewhere, or
 * finished for the day.
 *
 * Three states rather than a boolean, because «ish vaqti tugagan» and «mahalla
 * aylanishda» are different answers to "can I go and see them", and collapsing
 * them into "not available" throws away the one that makes field work look like
 * work.
 */
export const PRESENCE_STATES = ['at_desk', 'away_working', 'off'];
export function derivePresence(lastDirection, workMode, at = null, now = new Date()) {
    if (lastDirection !== 'in')
        return 'off';
    // §7.5 — a check-in nobody closed is not somebody still at work. See
    // `isStaleCheckIn`; the session is READ as closed, never written closed.
    if (isStaleCheckIn(at, now))
        return 'off';
    return workMode === 'office' ? 'at_desk' : 'away_working';
}
/**
 * §7.2's PIN fallback — "always available, never hidden, never presented as
 * inferior".
 *
 * SIX DIGITS, NOT FOUR. A four-digit PIN on a terminal in a public lobby, with
 * roughly seven candidate staff to try it against, is a number somebody can
 * walk through in an afternoon; six is a million. The cost is two extra taps
 * twice a day, which is not the reason anybody abandons an attendance system.
 *
 * §18 risk 4 is why this path exists at all: mahalla lobbies are badly lit, and
 * "the PIN fallback is not a nicety; it is the thing that keeps the system
 * usable on day one."
 */
export const STAFF_PIN_LENGTH = 6;
export function isValidPinFormat(pin) {
    return new RegExp(`^\\d{${STAFF_PIN_LENGTH}}$`).test(pin);
}
/**
 * §7.8 — "repeated failed face attempts (possible impersonation)".
 *
 * The same reasoning covers the PIN, and harder: a PIN can be brute-forced and
 * a face cannot. Five attempts then a fifteen-minute lock — long enough to make
 * a search infeasible, short enough that somebody who genuinely mistyped is not
 * locked out of their own morning.
 *
 * THE LOCK IS PER PERSON, NOT PER KIOSK. Per-kiosk would let an attacker work
 * through one person's PIN by moving between terminals, and would lock a whole
 * mahalla out because one person forgot theirs.
 */
export const PIN_MAX_FAILURES = 5;
export const PIN_LOCKOUT_MINUTES = 15;
/**
 * §8.3 — consent states. `none` is the default and it is a real state: nobody
 * has been asked yet, which is different from having refused.
 */
export const CONSENT_STATES = ['none', 'signed', 'revoked'];
/**
 * §8.3 — "enrollment is blocked without recorded consent".
 *
 * Also a CHECK constraint (migration 0031). Both exist because the breach here
 * is a biometric taken from somebody who never agreed to it: a route can be
 * refactored and a bulk importer can be written next year by somebody who has
 * not read §8.3, and the database is the copy that does not care.
 */
export function mayEnrollFace(consent) {
    return consent === 'signed';
}
/**
 * §8.3 — "automatic deletion 30 days after deactivation".
 *
 * Counted from `deactivated_at`, not from `updated_at`: the latter moves when
 * somebody fixes a phone number, which would postpone the deletion of a
 * departed employee's face template indefinitely, silently, forever.
 */
export const FACE_DATA_RETENTION_DAYS_AFTER_DEACTIVATION = 30;
/** §7.4 — "one frame saved per check-in for audit, retention-bound (default 90 days)". */
export const ATTENDANCE_EVIDENCE_RETENTION_DAYS = 90;
// ---------------------------------------------------------------------------
// MBT §7.8 — ATTENDANCE ANOMALIES.
//
// "Each anomaly is a reviewable item with the evidence photo attached, NOT AN
// AUTOMATIC ACCUSATION."
//
// That clause governs everything here, including what is absent: there is no
// severity ladder, no score, and no rule that closes itself. A machine noticing
// a pattern in somebody's working day is the beginning of a conversation, and
// the system's whole job is to hand a human the reading and get out of the way.
// ---------------------------------------------------------------------------
/**
 * §7.8's six patterns, and WHICH FIVE SHIP.
 *
 * The same discipline §4.4's alert rules got: a rule that cannot be computed
 * honestly is ABSENT rather than approximated, and its absence is written down.
 *
 * NOT SHIPPING, and why:
 *
 *   `outside_shift_window` — needs §7.5's shift templates, which are P4 and are
 *   not built. Approximating "the shift" as 09:00–18:00 across 33 mahallas
 *   would flag every early cleaner and every late reception evening, and a
 *   queue that is 80% false positives is a queue nobody opens twice.
 *
 *   `zero_activity` — §7.8 says "checked in but zero recorded activity all
 *   day", and this system has no definition of "activity" for a yettiligi
 *   member. They do not handle murojaats in the panel; handlers do. Inventing a
 *   proxy — kiosk touches in their mahalla, say — would accuse somebody of
 *   idleness on the strength of whether CITIZENS turned up that day.
 */
export const ATTENDANCE_ANOMALY_RULES = [
    /**
     * A check-in and a check-out within minutes of each other. §7.8's "check-in
     * immediately followed by check-out".
     *
     * The single most common ordinary explanation is that somebody realised they
     * had left something in the car, so the queue copy says what was OBSERVED and
     * never what it means.
     */
    'checkin_checkout_immediate',
    /**
     * A day that opened and never closed. §7.5's "missing check-out", promoted
     * from a board flag to a reviewable item so that it reaches a timesheet
     * before the timesheet is wrong.
     */
    'no_checkout',
    /**
     * §7.8's "PIN used persistently by someone who has an enrolled face".
     *
     * NOT a suspicion of impersonation by default — §7.10's "recognition quality
     * trends" reads the same signal as a TEMPLATE problem: "a falling confidence
     * curve means the template needs re-enrollment". Same evidence, two readings,
     * and the innocent one is far more likely. The queue exists so a person picks.
     */
    'pin_despite_enrolled_face',
    /** §7.8's "repeated failed face attempts (possible impersonation)", PIN half. */
    'repeated_pin_failures',
    /** §7.8's "same employee checked in at two kiosks". */
    'two_kiosks_same_day',
];
/**
 * How close two events have to be to read as "immediately".
 *
 * FIVE MINUTES. Long enough that a genuine short visit — dropping off a form,
 * signing a book — is not flagged; short enough that it is not a working
 * period by any reading. The number is deliberately generous, because the cost
 * of a false positive here is a colleague being asked to explain themselves.
 */
export const ANOMALY_IMMEDIATE_MINUTES = 5;
/** §7.8's "persistently": three PIN check-ins in a fortnight by an enrolled face. */
export const ANOMALY_PIN_USES = 3;
export const ANOMALY_PIN_WINDOW_DAYS = 14;
/** Five wrong PINs in a day is the point at which a person should look. */
export const ANOMALY_PIN_FAILURES = 5;
/**
 * §7.8's three outcomes, and `explained` is FIRST among them on purpose.
 *
 * Most of these have an ordinary explanation. A queue whose only outcomes are
 * guilt and silence teaches reviewers to leave everything open, and an
 * always-full queue is the same as no queue.
 */
export const ANOMALY_RESOLUTIONS = ['explained', 'corrected', 'confirmed'];
// ---------------------------------------------------------------------------
// MBT §6 — BROADCAST: ANNOUNCEMENTS.
//
// §6.5: "One screen where an operator writes an emergency notice, picks
// severity, targets, previews it at true 1080×1920, and sends. Target: UNDER 60
// SECONDS FROM TYPING TO ON-SCREEN CITY-WIDE. This is the feature that
// justifies the whole system in a crisis."
//
// AND ITS UNCOMFORTABLE COROLLARY. A system that can put words on 35 public
// screens in one minute can put the WRONG words on 35 public screens in one
// minute. Every rule below is about the second half.
// ---------------------------------------------------------------------------
/**
 * §6.4's three severities, each with a DEFINED kiosk treatment. Not a free-text
 * level: a typo'd fourth value would render as nothing at all, on a screen
 * nobody is watching until it matters.
 */
export const ANNOUNCEMENT_SEVERITIES = ['emergency', 'warning', 'info'];
/**
 * §6.1's priority order on the idle screen, as a number — higher wins.
 *
 *   1. emergency announcement (takeover, animated) — overrides everything
 *   2. informational announcement (takeover or banner)
 *   3. ad playlist
 *   4. default attract content
 *
 * Ads are not built (§18 risk 5: commercial advertising on a hokimlik screen is
 * a political decision awaiting written policy), so tier 3 is currently empty
 * and `info` falls straight through to the attract screen behind it. The tier
 * is numbered anyway so that adding ads later does not renumber announcements.
 */
export const ANNOUNCEMENT_TIER = {
    emergency: 40,
    warning: 30,
    info: 20,
};
export const ATTRACT_TIER = 10;
export const ANNOUNCEMENT_PRESENTATION = {
    emergency: { takeover: true, sound: true, accent: 'crit', dismissible: false },
    warning: { takeover: true, sound: false, accent: 'warn', dismissible: true },
    /*
      ========================================================================
      `info` TAKES OVER TOO, as of the owner's ruling on 2026-08-19: "all types
      of eʼlonlar should be big screen ones".
      ========================================================================
  
      It was a strip at the foot of the screen. The reasoning then was that a
      notice which does not interrupt anybody costs nobody anything — but the
      owner's point is the one that actually decides it: a hokimlik publishes an
      announcement to be READ, and a band along the bottom of a kiosk is read by
      nobody. A notice nobody sees is not a gentler notice, it is an absent one.
  
      DISMISSIBLE IS NOW TRUE, and that pairing is not optional. A takeover that
      cannot be dismissed holds the machine until the notice expires — acceptable
      for an evacuation, indefensible for «the water will be off on Thursday».
      Emergency remains the ONLY severity a citizen cannot put aside, which is
      what makes it mean anything.
  
      So the three severities now differ in exactly two ways, and both are
      honest: whether it makes a sound, and whether you can dismiss it.
    */
    info: { takeover: true, sound: false, accent: 'info', dismissible: true },
};
/**
 * §6.4's optional image or video, and the numbers the composer has to state.
 *
 * ============================================================================
 * THE KIOSK IS 1080×1920 PORTRAIT. AN OPERATOR CANNOT BE EXPECTED TO KNOW THAT.
 * ============================================================================
 *
 * The recommended size is published in the composer rather than left implicit,
 * because the alternative is what always happens otherwise: somebody attaches a
 * 4000×3000 landscape photo from a phone, it letterboxes into two black bars,
 * and the notice that was supposed to convey urgency looks like a mistake. The
 * panel states the target, warns when the aspect is far from it, and still
 * accepts the file — a wrong-shaped image during an emergency beats no image.
 */
export const KIOSK_SCREEN_WIDTH = 1080;
export const KIOSK_SCREEN_HEIGHT = 1920;
/**
 * Images are RE-ENCODED server-side; video is not.
 *
 * The image path goes through sharp, which makes the stored file a fresh JPEG
 * with no original metadata and no original parser surface — the same argument
 * §2.3 makes for staff photos. Video has no equivalent without ffmpeg in the
 * API image, so the video path is a TRUST decision: only a `mbt.broadcast.
 * publish` holder (governor, content, admin) can upload one, the container is
 * capped, and the type allowlist is one entry long. That is stated plainly
 * rather than dressed up, because it is the weakest link in this feature.
 */
export const ANNOUNCEMENT_IMAGE_MIME = ['image/jpeg', 'image/png', 'image/webp'];
export const ANNOUNCEMENT_VIDEO_MIME = ['video/mp4'];
export const ANNOUNCEMENT_IMAGE_MAX_BYTES = 8 * 1024 * 1024;
/**
 * 64 MiB — the SAME ceiling as a citizen's murojaat video (`VIDEO_MAX_BYTES`),
 * because that is what every layer of this platform is already sized for:
 * `REQUEST_MAX_BYTES` is 68 MiB and the staging nginx carries
 * `client_max_body_size 68m` for exactly this number.
 *
 * The first cut said 24 MiB, reasoning that a kiosk on a mahalla ADSL line has
 * to pull the file before showing it and §6.5's budget is sixty seconds. That
 * was wrong twice over, and it cost an operator two 46-second uploads that
 * ended in a 413 (2026-08-18). First: the notice TEXT arrives within one poll
 * regardless — the video is decoration on a message that is already on screen.
 * Second: the kiosk streams the file over Range requests, so size delays only
 * its own first frame, not the notice. An arbitrary cap tighter than the
 * platform's own ceiling was a limit protecting nothing.
 */
/**
 * The first kiosk build that can mirror its screen.
 *
 * ============================================================================
 * WITHOUT THIS, AN OLD BUILD IS INDISTINGUISHABLE FROM A BROKEN ONE.
 * ============================================================================
 *
 * A kiosk on an earlier build sends no screenshot — not because anything is
 * wrong with it, but because the code does not exist there. The wall saw the
 * same thing it sees when a healthy kiosk stops mirroring (no frame) and drew
 * the same amber «tasvir eskirgan», which points an operator at a fault that
 * is not there and hides the one action that would actually fix the tile.
 *
 * So the panel compares the build it can already see on the tile against this,
 * and says «eski versiya» instead. It is in `@navoiy/domain` rather than in the
 * screen because it is a fact about the FLEET, not about a component — and
 * because this is the single line anybody has to find if the feature is ever
 * backported to an older build.
 */
export const KIOSK_SCREEN_MIRROR_MIN_VERSION = '0.6.11';
/**
 * Compare two kiosk versions NUMERICALLY, not as strings.
 *
 * `'0.6.9' < '0.6.10'` is FALSE under a plain string compare — «9» sorts after
 * «1» — so a naive check declares the entire fleet outdated on the day a tenth
 * patch ships, which is exactly when nobody would believe the panel again.
 *
 * Returns a negative number when `a` is older than `b`, as `localeCompare` does.
 */
export function compareKioskVersions(a, b) {
    return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
}
/*
  ============================================================================
  §7.4 — FACE MATCHING. The numbers, in the one place they may be written.
  ============================================================================

  D-MBT-19 held this feature back rather than pick a model unattended; the owner
  chose on 2026-08-19 and `packages/face-models/README.md` records what was
  chosen and what was measured. These are the thresholds that decide whether a
  person is signed in, and they are here because the API embeds and the KIOSK
  matches — two processes, one rule. A threshold that lived in each would drift,
  and the drift would look like the model getting worse.
*/
/** The descriptor's length. 128 floats — short, which matters when 225 of them ride in a kiosk cache. */
export const FACE_DESCRIPTOR_DIMS = 128;
/**
 * The model this fleet runs, by name and version.
 *
 * D-MBT-12: "a fleet silently running two models is a debugging nightmare".
 * Stored on every enrolled row as `face_template_version` so a template
 * embedded by an older model can be found and re-enrolled rather than compared
 * against descriptors it cannot be compared with.
 */
export const FACE_MODEL_ID = 'faceapi-recognition-1.7.15';
export const FACE_MODEL_VERSION = 1;
/**
 * How close two descriptors must be to be the same person.
 *
 * ============================================================================
 * 0.52, NOT face-api's CONVENTIONAL 0.6, AND THE DIRECTION IS THE ARGUMENT.
 * ============================================================================
 *
 * The measurement in the models README is the reason: two DIFFERENT people
 * scored 0.500 on the library's own sample images. At the conventional 0.6 they
 * would have matched each other. That number is tuned for photo search, where a
 * false accept costs a wrong thumbnail.
 *
 * Here a false ACCEPT signs somebody in who is not in the building — an
 * attendance record that is a lie, on a government system, attributed to a
 * named person. A false REJECT costs one retry or a PIN. The two errors are not
 * comparable and the threshold should not be set as if they were.
 *
 * It is deliberately NOT low enough to be unusable: 0.52 still admits ordinary
 * day-to-day variation — glasses, a beard trimmed, the mahalla's lighting.
 * Re-tune it against real enrolments on real hardware, and when you do, move
 * this constant and nothing else.
 */
export const FACE_MATCH_MAX_DISTANCE = 0.52;
/**
 * The margin a match must win by.
 *
 * The nearest template must beat the SECOND nearest by this much, or the answer
 * is «I am not sure» rather than the closest name. Without it, two colleagues
 * who genuinely resemble each other resolve to whichever is fractionally
 * closer, every time, with full confidence — and the person wrongly signed in
 * never finds out.
 */
export const FACE_MATCH_MIN_MARGIN = 0.06;
/** The lowest detector score an enrolment photo may have. Below it, ask for a better picture. */
export const FACE_ENROL_MIN_SCORE = 0.7;
/**
 * The LAST-RESORT bar, used only after every pass at `FACE_ENROL_MIN_SCORE`
 * has found nothing (owner directive, 2026-09-02: Face ID must work from the
 * photographs already in the directory).
 *
 * Those photographs are 512 px headshots re-encoded by the directory upload,
 * many of them phone pictures stored sideways because EXIF orientation was
 * dropped. The tiny detector scores such a face in the fifties and sixties
 * and the strict bar refused about half the staff. A face the detector is
 * 50 % sure of is still one face, and the descriptor is computed by a
 * different network that does not inherit that doubt; what the score
 * actually protects against — a smudge in the background counted as a
 * second person — is still enforced, because a lenient pass that finds MORE
 * than one face is skipped rather than trusted.
 *
 * The score is stored in `face_quality` and the pass in the audit row, so a
 * lenient enrolment is visible to anyone asking why a match was weak.
 */
export const FACE_ENROL_FALLBACK_MIN_SCORE = 0.5;
/**
 * How long a kiosk may keep a template without a successful refresh.
 *
 * D-MBT-11, verbatim: "A kiosk offline for a month must not keep
 * authenticating somebody whose consent was revoked or whose employment ended
 * three weeks ago." Seven days.
 */
export const FACE_TEMPLATE_TTL_HOURS = 7 * 24;
/**
 * The largest enrolment photograph accepted.
 *
 * 8 MiB. An enrolment photo is a picture somebody took on a phone and did not
 * resize — routinely 3–6 MB — and a limit tuned for the camera wall's 40 KB
 * frames would leave an operator unable to enrol anybody without finding image
 * editing software first.
 */
export const FACE_PHOTO_MAX_BYTES = 8 * 1024 * 1024;
/** Euclidean distance between two descriptors. The whole of «is this them». */
export function faceDistance(a, b) {
    if (a.length !== b.length)
        return Number.POSITIVE_INFINITY;
    let sum = 0;
    for (let i = 0; i < a.length; i += 1) {
        const d = (a[i] ?? 0) - (b[i] ?? 0);
        sum += d * d;
    }
    return Math.sqrt(sum);
}
/**
 * The best match among candidates, or null when there is not one.
 *
 * Returns null in THREE cases, and they are different failures wearing the same
 * answer on purpose — the kiosk must not tell somebody standing at a machine
 * which of these happened, because "close, but not quite you" is a hint about
 * whose face is enrolled.
 *
 *   · nothing within `FACE_MATCH_MAX_DISTANCE`  — not enrolled, or not them
 *   · the runner-up is too close                — ambiguous; ask for a PIN
 *   · a candidate embedded by another model     — not comparable at all
 */
export function bestFaceMatch(probe, candidates) {
    const scored = candidates
        .filter((c) => c.modelVersion === FACE_MODEL_VERSION && c.descriptor.length === probe.length)
        .map((candidate) => ({ candidate, distance: faceDistance(probe, candidate.descriptor) }))
        .sort((a, b) => a.distance - b.distance);
    const best = scored[0];
    if (!best || best.distance > FACE_MATCH_MAX_DISTANCE)
        return null;
    const runnerUp = scored[1];
    if (runnerUp && runnerUp.distance - best.distance < FACE_MATCH_MIN_MARGIN)
        return null;
    return best;
}
/**
 * Distance as a 0–1 confidence, for `attendance_events.confidence`.
 *
 * The column exists because §7.10 wants "recognition quality trends" — a
 * falling curve means a template is going stale. It is a MONOTONE rescaling of
 * the distance and nothing more: it is not a probability, and it must never be
 * shown to a citizen or used to justify a match the threshold refused.
 */
export function faceConfidence(distance) {
    const scaled = 1 - distance / FACE_MATCH_MAX_DISTANCE;
    return Math.max(0, Math.min(1, Number(scaled.toFixed(3))));
}
export const ANNOUNCEMENT_VIDEO_MAX_BYTES = 64 * 1024 * 1024;
/**
 * The size past which a video is worth warning an operator about.
 *
 * 12 MiB. NOT a limit — the cap is 64 MiB and stays there. This is the point at
 * which a kiosk on a mahalla line can no longer fetch the file inside a slot,
 * so the first showing is skipped while it downloads in the background. The
 * owner's first real booking was 54 MiB and showed nothing on its first passes;
 * the composer now says so BEFORE the booking is made rather than leaving the
 * operator to discover it on a public screen.
 */
export const ANNOUNCEMENT_VIDEO_WARN_BYTES = 12 * 1024 * 1024;
/** Which kind a stored object is, from its mime and nothing else. */
export function announcementMediaKind(mime) {
    if (ANNOUNCEMENT_IMAGE_MIME.includes(mime))
        return 'image';
    if (ANNOUNCEMENT_VIDEO_MIME.includes(mime))
        return 'video';
    return null;
}
// ---------------------------------------------------------------------------
// §6.2 — the ad slot's three numbers.
// ---------------------------------------------------------------------------
/**
 * HOW LONG one showing lasts.
 *
 * 15 s by default and 60 s at the ceiling. The ceiling is the load-bearing
 * number: an ad is playing on a machine somebody may be walking towards, and
 * past a minute the kiosk has stopped being a public service with a sponsor and
 * started being a screen that makes citizens wait. The kiosk abandons a play
 * the moment anything is touched, so the cap is about the person who has not
 * touched it yet — the one deciding whether this machine is worth approaching.
 */
export const AD_DEFAULT_DURATION_SECONDS = 15;
export const AD_MIN_DURATION_SECONDS = 3;
export const AD_MAX_DURATION_SECONDS = 60;
/**
 * HOW OFTEN — the minimum gap between plays on one kiosk.
 *
 * A GAP, NOT A QUOTA, and the difference matters when somebody is invoiced. A
 * quota ("6 plays an hour") is a promise the kiosk cannot keep: a machine with
 * a queue of citizens is not idle, and an ad that insisted on its quota would
 * have to interrupt one of them. A gap degrades honestly instead — a busy kiosk
 * simply plays it less, `kiosk_ad_plays` records what actually ran, and the
 * invoice is built from that rather than from the schedule's intention.
 */
export const AD_DEFAULT_EVERY_MINUTES = 10;
export const AD_MIN_EVERY_MINUTES = 1;
export const AD_MAX_EVERY_MINUTES = 720;
/** Minutes since local midnight; 1439 is 23:59. */
export const AD_DAY_END_MINUTE = 1439;
/**
 * WHEN — is this ad allowed on screen at this instant?
 *
 * Evaluated on the KIOSK against its own wall clock, for the same reason §6.4's
 * expiry is: a mahalla machine whose connection dropped an hour ago must still
 * stop showing an ad when its daily window closes. A schedule enforced only by
 * the server is a schedule that fails open on exactly the machines nobody is
 * looking at.
 *
 * The daily window uses LOCAL minutes because that is what an operator means.
 * "Show it between 09:00 and 18:00" is a statement about the building's day,
 * not about UTC, and the kiosk standing in that building is the only participant
 * that knows what time it is there.
 */
export function adIsInWindow(ad, now = new Date()) {
    if (ad.paused_at)
        return false;
    const at = now.getTime();
    if (at < new Date(ad.starts_at).getTime())
        return false;
    if (at >= new Date(ad.ends_at).getTime())
        return false;
    const minuteOfDay = now.getHours() * 60 + now.getMinutes();
    return minuteOfDay >= ad.daily_start_minute && minuteOfDay <= ad.daily_end_minute;
}
/**
 * Has the gap elapsed since this kiosk last played it?
 *
 * `lastPlayedAt === null` means never, which is due immediately — a new booking
 * should appear on the next idle screen rather than waiting out a full interval
 * for no reason.
 */
export function adIsDue(lastPlayedAt, everyMinutes, now = new Date()) {
    if (lastPlayedAt === null)
        return true;
    return now.getTime() - lastPlayedAt.getTime() >= everyMinutes * 60_000;
}
/** «09:00» from 540, for the composer and the schedule summary. */
export function formatMinuteOfDay(minute) {
    const hours = Math.floor(minute / 60);
    const minutes = minute % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}
/**
 * §6.4 — "auto-expiry mandatory (no announcement lives forever)".
 *
 * The default the composer offers, not a cap. Four hours is long enough for a
 * water shut-off to be over and short enough that forgetting to take one down
 * is self-correcting — which is the actual failure mode: a notice from last
 * March still on a screen in September teaches every citizen who walks past
 * that the screens are not to be believed, and the next REAL emergency inherits
 * that.
 */
export const ANNOUNCEMENT_DEFAULT_HOURS = 4;
/**
 * The longest window the composer will accept without a deliberate override.
 *
 * §6.4 forbids "forever" and does not say where forever begins. A week: past
 * that, the thing being described is a policy change rather than an event, and
 * it belongs in the directory or the attract content instead of in a channel
 * reserved for things that are happening now.
 */
export const ANNOUNCEMENT_MAX_HOURS = 24 * 7;
/**
 * Is this announcement live at `now`?
 *
 * Taken-down beats the window, and both beat severity. Derived rather than
 * stored for the reason every other state in this system is: a cached `is_live`
 * and the clock behind it drift apart the first time a job dies, and the
 * cached one is what ends up on a public screen.
 */
export function isAnnouncementLive(input, now = new Date()) {
    if (input.takenDownAt !== null)
        return false;
    return input.startsAt.getTime() <= now.getTime() && now.getTime() < input.expiresAt.getTime();
}
/**
 * §3.1 AS IT ALREADY READS — the appeal lock covers this surface by name:
 * it "suppresses broadcast audio and blocks takeovers while a citizen records
 * a murojaat".
 *
 * So a TAKEOVER waits, exactly as a disruptive command does (D-MBT-10), and for
 * the same reason: a full-screen evacuation notice landing on somebody halfway
 * through describing their problem destroys that appeal, and they have no idea
 * why the screen changed.
 *
 * SINCE 2026-08-19 THIS IS TRUE OF ALL THREE SEVERITIES, because all three are
 * takeovers now. That is not a special case bolted on — the function asks the
 * presentation table whether the notice covers the screen, and every notice
 * that covers the screen must wait for the same reason. When `info` was a
 * banner it did not wait, correctly, because it covered nothing.
 *
 * THE KIOSK DECIDES THIS LOCALLY. §3.1 gives the server no authority to
 * override it, so there is no field on the wire that could.
 */
export function announcementDefersDuringCapture(severity) {
    return ANNOUNCEMENT_PRESENTATION[severity].takeover;
}
/** §6.4's four locales. uz-Latn is the fallback and is therefore required. */
export const ANNOUNCEMENT_FALLBACK_LOCALE = 'uz-Latn';
/**
 * Which locales an announcement is missing.
 *
 * §6.4: "a missing locale falls back to uz-Latn and is FLAGGED IN THE COMPOSER,
 * never shown empty". The flag is the whole feature — it is the difference
 * between "we chose not to translate at 3 a.m." and "this screen is broken",
 * and only the composer can tell an operator which one they are choosing.
 */
export function missingAnnouncementLocales(text) {
    return LOCALES.filter((locale) => {
        if (locale === ANNOUNCEMENT_FALLBACK_LOCALE)
            return false;
        const entry = text[locale];
        return !entry?.title?.trim() || !entry?.body?.trim();
    });
}
/** §4.5's lifecycle, as stored in `kiosk_commands.status` (migration 0027). */
export const KIOSK_COMMAND_STATUSES = [
    'queued',
    'delivered',
    'deferred',
    'acked',
    'failed',
    'expired',
];
/**
 * §15.7's "kiosks working" figure. `healthy` and `warning` count; a kiosk in a
 * maintenance window does NOT.
 *
 * That last one is a judgement and it goes the honest way: a machine nobody can
 * use is not serving citizens, whatever the reason, and a governor reading
 * «31 ishlamoqda» is asking how many screens a citizen could walk up to — not
 * how many have an excuse.
 */
export function isKioskWorking(status) {
    return status === 'healthy' || status === 'warning';
}
// ---------------------------------------------------------------------------
// §5 Credential shapes
// ---------------------------------------------------------------------------
export const DEVICE_KEY_PREFIX = 'nvk_';
export const INSTALL_TOKEN_PREFIX = 'nvi_';
/**
 * §5 (D-032 GAP 3) — the bot→API internal service credential. 32 random bytes
 * printed as `nvb_<base64url43>`, held only in `/opt/navoiy/.env`, compared in
 * constant time against the SHA-256 of the configured value. **Not argon2**: a
 * 256-bit random secret has no offline-guessing surface, and a 64 MiB KDF on
 * every citizen submission is cost without a threat.
 */
export const BOT_SERVICE_TOKEN_PREFIX = 'nvb_';
export const BOT_SERVICE_TOKEN_RE = /^nvb_[A-Za-z0-9_-]{43}$/;
/** §10.3 — the only hosts for which a plaintext base URL is permissible. */
export const INTERNAL_PLAINTEXT_HOSTS = ['api', '127.0.0.1', 'localhost'];
/** §5 — one-time enrollment code, 8 chars, 24 h TTL, single use. */
export const ENROLL_CODE_LENGTH = 8;
export const ENROLL_CODE_TTL_HOURS = 24;
export const ADMIN_SESSION_COOKIE = '__Host-nvadmin';
export const ADMIN_SESSION_IDLE_MINUTES = 30;
export const ADMIN_SESSION_ABSOLUTE_HOURS = 12;
// ---------------------------------------------------------------------------
// §12M.1 Telegram Mini App session — `webAppSession`
//
// The Mini App exchanges Telegram's `initData` ONCE for a short-lived bearer,
// rather than presenting initData per request. Two facts force that shape and
// both are probe-verified (F-17): `auth_date` is stamped at launch and NEVER
// refreshes, so a freshness window tight enough to be a replay control would
// 401 a citizen halfway through typing a 4000-character appeal; and initData
// carries `first_name`/`last_name`/`username`, which §10.4 forbids in logs and
// which a per-request header would put in every proxy buffer.
// ---------------------------------------------------------------------------
export const WEBAPP_SESSION_TOKEN_PREFIX = 'nvw_';
export const WEBAPP_SESSION_TOKEN_RE = /^nvw_[A-Za-z0-9_-]{43}$/;
/** §12M.1.3 step 1 — the hard cap on the `init_data` body field. */
export const WEBAPP_INITDATA_MAX_BYTES = 4096;
/** §12M.1.3 step 6 — `now - auth_date` may not exceed this. */
export const WEBAPP_INITDATA_MAX_AGE_S = 900;
/** §12M.1.3 step 6 — tolerated clock skew in the other direction. */
export const WEBAPP_INITDATA_SKEW_S = 60;
/** §12M.1.2 — 30 min idle (slides) / 4 h absolute (never slides). */
export const WEBAPP_SESSION_IDLE_MINUTES = 30;
export const WEBAPP_SESSION_ABSOLUTE_HOURS = 4;
/**
 * F-16 — `secret_key = HMAC_SHA256(key="WebAppData", msg=<bot_token>)`.
 *
 * The constant is the KEY and the bot token is the MESSAGE. That is the way
 * round Telegram documents it and the way round that is easy to get wrong; it
 * is named here so both the implementation and its test read the same constant.
 */
export const WEBAPP_INITDATA_HMAC_KEY = 'WebAppData';
/**
 * §12M.1.3 step 3 — the fields excluded from `data_check_string`.
 *
 * **`hash` ONLY, and this is an EMPIRICAL correction to the spec text.**
 *
 * §12M's F-16 reads "all received fields **except `hash`** (and `signature`)".
 * The parenthesis is wrong for the HMAC path, and it was found the only way it
 * could be found — with a real launch from a real phone:
 *
 *   - real Telegram initData (which now carries a `signature` field) failed
 *     with `bad_hash` while synthetic initData signed the same way passed;
 *   - the two agreed with each other because BOTH used this constant. That
 *     self-consistency is exactly why every unit test stayed green through a
 *     real defect, and it is why the test that pins this value exists.
 *
 * The exclusion of `signature` belongs to F-20's **Ed25519 third-party**
 * verification, which we do not use — we hold the bot token, so the HMAC of
 * F-16 is our check, and Telegram computes that HMAC over every received field
 * except `hash` itself, `signature` included.
 *
 * DO NOT "fix" this back to the spec's parenthesis. Doing so rejects every real
 * Telegram launch while leaving all synthetic tests passing.
 */
export const WEBAPP_INITDATA_EXCLUDED_FIELDS = ['hash'];
/** F-14 — the header Telegram echoes on every webhook request. */
export const TELEGRAM_SECRET_HEADER = 'X-Telegram-Bot-Api-Secret-Token';
// ---------------------------------------------------------------------------
// DEPLOYMENT TIER — which HOST this is, as distinct from how Node is running.
//
// ===========================================================================
// WHY THIS EXISTS: `NODE_ENV=production` WAS ANSWERING TWO DIFFERENT QUESTIONS.
// ===========================================================================
//
// Staging runs `NODE_ENV=production` (deploy/README.md), because it is a
// production BUILD — optimised, no dev middleware, the same image the real host
// runs. Two guards were keyed on that variable and they wanted opposite answers
// from it:
//
//   1. THE FOREIGN-PROCESSOR LEGAL GATES (`TRANSCRIBE_PROVIDER=gemini`,
//      `ANALYZE_PROVIDER=gemini`) want staging to be treated AS IF production:
//      the reason string must be present there too, and it is — D-041's
//      "STAGING-SYNTHETIC-DATA-ONLY". Correct behaviour, and it must not change.
//
//   2. TESTING OPEN-ENROLLMENT MODE (`ENROLL_OPEN_MODE`) wants the opposite. It
//      refuses unconditionally under `NODE_ENV=production` with no override, by
//      design — but that made the feature DEAD ON STAGING, which is the only
//      place it was ever for. A test kiosk could not self-enrol on the one host
//      test kiosks live on.
//
// One variable cannot mean both. `DEPLOYMENT_TIER` names the host directly, and
// each guard now keys on the tier set it actually cares about:
//
//   guard                          fires on
//   -----------------------------  ------------------------------------------
//   enroll open mode (no override) production ONLY  → staging works again
//   transcribe/analyze legal gate  production AND staging → unchanged, no
//                                  weakening; dev is still exempt as today
//
// DERIVATION, so no existing deployment changes behaviour by omission:
// `NODE_ENV=production` with `DEPLOYMENT_TIER` unset stays `production` — the
// strictest reading — and staging opts DOWN by setting it explicitly. A host
// that forgets the variable is treated as the real thing, which is the failure
// direction that cannot hurt a citizen.
// ---------------------------------------------------------------------------
export const DEPLOYMENT_TIERS = ['production', 'staging', 'dev'];
export function isDeploymentTier(value) {
    return DEPLOYMENT_TIERS.includes(value);
}
/**
 * The tier, from `DEPLOYMENT_TIER` when it is set and valid, otherwise derived
 * from `NODE_ENV`.
 *
 * An INVALID `DEPLOYMENT_TIER` falls back to the derivation rather than
 * throwing, and the caller reports it: a typo'd `DEPLOYMENT_TIER=stagng` must
 * not silently become the loosest tier, and it must not take a government API
 * off the air either. Falling back to the `NODE_ENV` derivation gives the
 * strict answer on a production host and lets the boot log say the value was
 * not understood.
 */
export function resolveDeploymentTier(env) {
    const raw = (env.DEPLOYMENT_TIER ?? '').trim();
    if (raw.length > 0 && isDeploymentTier(raw)) {
        return { tier: raw, explicit: true, malformed: null };
    }
    const derived = env.NODE_ENV === 'production' ? 'production' : 'dev';
    return { tier: derived, explicit: false, malformed: raw.length > 0 ? raw : null };
}
/**
 * Tiers on which sending citizen content to a processor OUTSIDE Uzbekistan
 * requires a named reason (§10.1 rule 1, F-8).
 *
 * BOTH real hosts, deliberately. Staging carries synthetic data only (§11.4,
 * D-041) and could arguably be exempt — but the exemption would have to be
 * enforced by trusting that no real appeal ever reaches staging, and the cost
 * of the gate there is one environment variable that already exists. Widening
 * this to exclude staging would be the weakening this split was written to
 * avoid.
 */
export const FOREIGN_PROCESSOR_GATED_TIERS = ['production', 'staging'];
export function requiresForeignProcessorReason(tier) {
    return FOREIGN_PROCESSOR_GATED_TIERS.includes(tier);
}
/**
 * The gate itself, as one predicate, so the worker's boot refusal and the admin
 * panel's provider status cannot disagree about whether analysis is running.
 *
 * They are two services reading the same `.env` (compose gives `api` and
 * `worker` the same `env_file`), and without this they would each re-implement
 * the condition. The failure that would produce is specific and bad: a worker
 * that refused to start while the AI screen cheerfully reported `provider: on`
 * and an empty board — which §14.4.5 rule 5 says must never be shown, because
 * an empty board reads as "no risk found".
 *
 * The reason string is NEVER validated for content and cannot be. It exists so
 * that enabling a foreign processor for citizen data is a deliberate act that
 * names its own legal basis, recorded in the deployment's configuration and
 * visible to the next person who reads it.
 */
export function foreignProcessorPermitted(tier, reason) {
    return !requiresForeignProcessorReason(tier) || reason.trim().length > 0;
}
// ---------------------------------------------------------------------------
// §2.7 Retention classes — periods live in deploy/retention.json and are
// `null` (no automatic deletion) until the owner decides (Open question 1).
// ---------------------------------------------------------------------------
export const RETENTION_CLASSES = ['R-APPEAL', 'R-MEDIA', 'R-CHANNEL', 'R-AUDIT'];
// ---------------------------------------------------------------------------
// §10.2 Consent
// ---------------------------------------------------------------------------
/** Bumped whenever the notice text changes (a content + code change together). */
export const CONSENT_VERSION = '2026-08-12.v1';
export const SHA256_HEX_RE = /^[0-9a-f]{64}$/;
//# sourceMappingURL=index.js.map